import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import {useNavigate} from 'react-router-dom'
import styled from 'styled-components'
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    LinearProgress,
    MenuItem,
    Radio,
    RadioGroup,
    Snackbar,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import RefreshIcon from '@mui/icons-material/Refresh'
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined'
import SendIcon from '@mui/icons-material/Send'
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined'
import {HistoryContext} from '../../../contexts/history'
import {LevantamentoApiService} from '../../../services/LevantamentoApi.service'
import {
    Hipotese74,
    AlternativaCreate,
    AlternativaOut,
    AlternativaStatus,
    ContratacaoPncp,
    Diferencial,
    LevantamentoDetail,
    LevantamentoResumo,
    LevantamentoStatus,
    MensagemOut,
    Requisito,
    SinalModalidade,
    SolucaoMercado,
    TipoAlternativa,
} from '../../../types/levantamento'
import {getCurrentColors} from '../../../utils/colors'

const COLORS = getCurrentColors()
const ACCENT = COLORS?.primariaEscura || '#3394EF'

// Progresso em tempo real é PUSH via user-notifier (regra da casa: nunca
// polling). O intervalo abaixo é usado SOMENTE pelo fallback de dev local,
// gated por VITE_LEVANTAMENTO_POLL === 'true' (default OFF, nunca em prod).
const DEV_POLL_INTERVAL_MS = 4000
// Busca do relatorio.html: tentativas automáticas limitadas com backoff — em
// falha persistente entra em estado terminal com botão de tentar novamente
// (nunca loop de retry na velocidade da rede).
const RELATORIO_MAX_TENTATIVAS = 3
const RELATORIO_BACKOFF_MS = 2000

// Tokens de INSTRUMENTO de contratação (ata/registro/preços/adesão/carona/SRP):
// o MODO de contratar, nunca o OBJETO. Fail-safe local do handoff p/ o Ata
// Inteligente — o backend já expõe `termo_ata` limpo; isto cobre alternativas
// antigas (sem o campo), garantindo que a busca de atas nunca receba o termo
// poluído "ata registro preços <objeto>" (que dá ZERO resultado no PNCP).
const TOKENS_INSTRUMENTO_ATA = new Set([
    'ata', 'atas', 'registro', 'registros', 'preco', 'precos', 'adesao',
    'adesoes', 'carona', 'caronas', 'srp', 'irp',
])
const _normalizarTokenAta = (p: string): string =>
    p
        .replace(/[^0-9A-Za-zÀ-ÖØ-öø-ÿ]/g, '')
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
// COMPAT DATADA (introduzida em 2026-08) — NÃO é regra de negócio: a limpeza
// oficial mora no backend, que persiste `termo_ata` já limpo. Esta função só
// cobre a JANELA DE MIGRAÇÃO: levantamentos criados ANTES daquele deploy não
// têm `termo_ata`, e sem ela o handoff do Ata Inteligente cairia no
// `termo_busca_pncp` poluído ("ata registro preços <objeto>" → ZERO no PNCP).
// Enquanto existir, o front pode divergir do backend em casos de borda (a
// lista de tokens aqui é fixa; a de lá evolui) — logo, quanto antes sair,
// melhor.
// CONDIÇÃO DE REMOÇÃO: quando não houver mais alternativa antiga alcançável —
// isto é, quando na base não existir alternativa com `termo_busca_pncp`
// preenchido e `termo_ata` nulo (levantamentos são de vida curta; conferir
// após ~1 ciclo de retenção). Ao remover, apagar também TOKENS_INSTRUMENTO_ATA
// e _normalizarTokenAta, e usar `alt.termo_ata` direto no handoff (verificarAtas).
const limparTermoInstrumento = (termo?: string | null): string => {
    const t = (termo || '').trim()
    if (!t) return ''
    const limpos = t
        .split(/\s+/)
        .filter((p) => !TOKENS_INSTRUMENTO_ATA.has(_normalizarTokenAta(p)))
    // Se sobrou objeto, usa; senão devolve o termo original (nunca vazio).
    return limpos.length ? limpos.join(' ') : t
}

type View = 'list' | 'detail'

const STEP_LABELS = [
    'Problema',
    'Alternativas',
    'Investigação',
    'Escolha',
    'Relatório',
]

// Fluxo NOVO (usuário no comando): a IA compara/sugere, o usuário decide em
// cada passo. Levantamentos antigos já investigados seguem no stepper antigo.
const STEP_LABELS_NOVO = [
    'Problema',
    'Conceitos',
    'Soluções',
    'Comparação',
    'Escolha',
    'Relatório',
]

const Container = styled.div`
    flex: 1;
    height: 100vh;
    overflow-y: auto;
    padding: 28px 40px;
    background-color: #f6f8fa;
    @media (max-width: 640px) {
        padding: 18px 14px;
    }
`
const Card = styled.div`
    background: #ffffff;
    border: 1px solid #e6e9ee;
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 22px;
`
const SectionTitle = styled.h3`
    font-size: 17px;
    font-weight: 700;
    color: #1f2b3a;
    margin: 0 0 16px 0;
`
const FieldLabel = styled.label`
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #3a3a3a;
    margin: 0 0 6px 2px;
`
const Th = styled.th`
    text-align: left;
    font-size: 12px;
    font-weight: 700;
    color: #7a8896;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    padding: 10px 12px;
    border-bottom: 1px solid #eef1f4;
`
const Td = styled.td`
    padding: 12px;
    border-bottom: 1px solid #f1f3f6;
    font-size: 14px;
    color: #2a3340;
    vertical-align: middle;
`
const ChatBox = styled.div`
    border: 1px solid #e6e9ee;
    border-radius: 12px;
    background: #fbfcfe;
    padding: 16px;
    max-height: 420px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
`
const Bubble = styled.div<{papel: string}>`
    max-width: 78%;
    padding: 10px 14px;
    border-radius: 12px;
    font-size: 14px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
    align-self: ${({papel}) =>
        papel === 'USUARIO' ? 'flex-end' : 'flex-start'};
    background: ${({papel}) =>
        papel === 'USUARIO'
            ? '#eef5ff'
            : papel === 'IA'
            ? '#ffffff'
            : 'transparent'};
    border: ${({papel}) =>
        papel === 'SISTEMA' ? 'none' : '1px solid #e0e4ea'};
    color: ${({papel}) => (papel === 'SISTEMA' ? '#7a8896' : '#2a3340')};
    font-style: ${({papel}) => (papel === 'SISTEMA' ? 'italic' : 'normal')};
`
const BubbleAutor = styled.div`
    font-size: 11px;
    font-weight: 700;
    color: #7a8896;
    margin-bottom: 4px;
`
const AltCard = styled.div<{escolhida?: boolean; recomendado?: boolean}>`
    border: 1.5px solid
        ${({escolhida, recomendado}) =>
            escolhida ? ACCENT : recomendado ? '#2e7d32' : '#e0e4ea'};
    background: ${({escolhida, recomendado}) =>
        escolhida ? '#eef5ff' : recomendado ? '#f2fbf4' : '#ffffff'};
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 14px;
`
const FaixaGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(90px, 1fr));
    gap: 10px;
    margin-top: 12px;
    @media (max-width: 820px) {
        grid-template-columns: repeat(2, 1fr);
    }
`
const FaixaItem = styled.div`
    background: #f6f8fa;
    border: 1px solid #eef1f4;
    border-radius: 10px;
    padding: 10px 12px;
`
const SolucaoRow = styled.div`
    background: #fbfcfe;
    border: 1px solid #eef1f4;
    border-radius: 10px;
    padding: 10px 12px;
    margin-bottom: 8px;
`

const currency = (v?: number | null) =>
    v == null
        ? '—'
        : new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
          }).format(Number(v))
const dateTime = (iso?: string | null) => {
    if (!iso) return '—'
    // Backend grava UTC sem offset; trata como UTC e exibe no fuso de Brasília.
    const s = /[Zz]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z'
    return new Date(s).toLocaleString('pt-BR', {timeZone: 'America/Sao_Paulo'})
}

const STATUS_LABEL: Record<LevantamentoStatus, string> = {
    RASCUNHO: 'Rascunho',
    CONVERSANDO: 'Conversando com a Licito.guru IA',
    ALTERNATIVAS_PRONTAS: 'Alternativas propostas',
    CONCEITOS_PRONTOS: 'Conceitos comparados — a escolha é sua',
    INVESTIGANDO: 'Investigando o mercado',
    SOLUCOES_PRONTAS: 'Soluções identificadas — selecione',
    PARECER_PRONTO: 'Parecer comparativo pronto',
    COMPARANDO: 'Comparando alternativas',
    CONCLUIDO: 'Concluído',
    ERRO: 'Erro',
}
const statusColor = (
    s: LevantamentoStatus
): 'default' | 'info' | 'success' | 'error' => {
    if (s === 'CONCLUIDO') return 'success'
    if (s === 'ERRO') return 'error'
    if (s === 'RASCUNHO') return 'default'
    return 'info'
}

const TIPO_LABEL: Record<TipoAlternativa, string> = {
    COMPRA: 'Compra',
    LOCACAO: 'Locação',
    SERVICO: 'Serviço',
    HIBRIDO: 'Híbrido',
    ATA_CARONA: 'Adesão a ata (carona)',
}
const TIPOS: TipoAlternativa[] = [
    'COMPRA',
    'LOCACAO',
    'SERVICO',
    'HIBRIDO',
    'ATA_CARONA',
]
const ALT_STATUS_LABEL: Record<AlternativaStatus, string> = {
    PROPOSTA: 'Proposta',
    EM_INVESTIGACAO: 'Investigando…',
    INVESTIGADA: 'Investigada',
    DESCARTADA: 'Descartada',
    ESCOLHIDA: 'Escolhida',
}
const altStatusColor = (
    s: AlternativaStatus
): 'default' | 'info' | 'success' | 'warning' => {
    if (s === 'INVESTIGADA') return 'success'
    if (s === 'EM_INVESTIGACAO') return 'info'
    if (s === 'ESCOLHIDA') return 'success'
    if (s === 'DESCARTADA') return 'warning'
    return 'default'
}

// Mensagem amigável sem expor detalhes técnicos.
const msgDeErro = (e: any): string => {
    const status = e?.response?.status
    if (status === 402)
        return 'Créditos de Consulta insuficientes para esta ação. Fale com o administrador da sua organização.'
    if (status === 401 || status === 403)
        return 'Sua sessão expirou. Atualize a página e entre novamente.'
    if (status === 429)
        return 'Uma repesquisa já foi disparada há pouco — aguarde alguns minutos antes de tentar de novo.'
    return 'Não foi possível concluir a ação. Tente novamente em instantes.'
}

// Janela local do estado "repesquisando" (espelha REPESQUISA_COOLDOWN_S do
// backend, default 300s) — o término real chega sempre por push terminal.
const REPESQUISA_COOLDOWN_MS = 5 * 60_000

// Rede de segurança do push (caso real: push saiu 200 e a tela ficou parada):
// UM único refetch de reconciliação por estado de espera (padrão aprovado —
// nunca polling em loop).
const RECONCILIACAO_MS = 12_000

// Valor sentinela do cartão "Sem preferência" no RadioGroup da Escolha.
const SEM_PREFERENCIA = '__SEM_PREFERENCIA__'

// Aviso de responsabilidade (decisão do dono): UI apenas — o texto copiável
// segue limpo.
const AVISO_RESPONSABILIDADE =
    'A Licito.guru IA é uma ferramenta de apoio: a análise e a decisão são do ' +
    'agente público responsável, que deve verificar as informações antes de ' +
    'utilizá-las.'

const avisoResponsabilidade = (
    <Alert severity="info" sx={{mb: 2, borderRadius: '10px'}}>
        <Typography sx={{fontSize: 13}}>{AVISO_RESPONSABILIDADE}</Typography>
    </Alert>
)

// Frase DETERMINÍSTICA do sinal de modalidades (números reais do PNCP) —
// linguagem sempre de indício; a decisão é do gestor.
const fraseSinal = (sinal?: SinalModalidade | null): string => {
    if (!sinal) return ''
    if (sinal.sinal === 'POSSIVEL_INEXIGIBILIDADE' && sinal.n_total > 0) {
        const alvo = sinal.solucao ? ` da solução ${sinal.solucao}` : ''
        return (
            `${sinal.n_inexigibilidade} de ${sinal.n_total} contratação(ões)` +
            `${alvo} identificadas no PNCP foram registradas por inexigibilidade.`
        )
    }
    if (sinal.sinal === 'CAMINHO_LICITACAO') {
        const partes: string[] = []
        if (sinal.n_total > 0) {
            partes.push(
                `${sinal.n_competitivas} de ${sinal.n_total} contratação(ões) ` +
                    'identificadas no PNCP foram por modalidades competitivas'
            )
        }
        if (sinal.n_players) {
            partes.push(
                `${sinal.n_players} solução(ões) de mercado equivalentes identificadas`
            )
        }
        return (
            'Não há indício de inexigibilidade nas evidências coletadas' +
            (partes.length ? ` (${partes.join('; ')})` : '') +
            ' — o caminho provável é a licitação.'
        )
    }
    return ''
}

// Destaque visual só para Inexigibilidade (indício do art. 74) — espelha a
// classificação determinística do backend (domain/levantamento/modalidades.py).
const ehInexigibilidade = (nome?: string | null): boolean =>
    !!nome && nome.toLowerCase().includes('inexigibilidade')

// Rótulo PURO da contagem de contratações do PNCP — FONTE ÚNICA do card, do
// painel de evidência e da Escolha. Existem DOIS truncamentos possíveis e os
// DOIS merecem ressalva:
//  - saturado: a 1ª página do PNCP encheu — corte ESPERADO → "N+";
//  - incompleto: uma página FALHOU (PNCP instável) — IMPREVISTO → "≥N". É o
//    caso MAIS grave dos dois, porque o gestor não tem como estimar o que
//    ficou de fora; a ressalva é explícita ("consulta parcial").
// Podem coexistir (uma página falhou E a última encheu) → "≥N+". Sem flag
// algum, número NU: onde a coleta foi íntegra não se planta dúvida.
const rotuloContagem = (
    s: SolucaoMercado
): {numero: string; ressalva: string; incompleto: boolean} => {
    const total = s.contratacoes_pncp ?? 0
    const incompleto = !!s.contratacoes_pncp_incompleto
    const saturado = !!s.contratacoes_pncp_saturado
    let ressalva = ''
    if (incompleto) {
        ressalva = 'consulta parcial (PNCP instável — pode haver mais)'
    } else if (saturado) {
        ressalva = '(amostra — pode haver mais)'
    }
    return {
        numero: `${incompleto ? '≥' : ''}${total}${saturado ? '+' : ''}`,
        ressalva,
        incompleto,
    }
}

// A ressalva renderizada — MESMO texto em todos os pontos que exibem a
// contagem (card, painel e Escolha), para nunca haver um lugar onde o número
// pareça fechado. `span` só existe porque no painel ela vive DENTRO de um
// parágrafo (<p> em <p> é HTML inválido).
const RessalvaContagem = ({s, span}: {s: SolucaoMercado; span?: boolean}) => {
    const {ressalva, incompleto} = rotuloContagem(s)
    if (!ressalva) return null
    return (
        <Typography
            component={span ? 'span' : 'p'}
            sx={{
                fontSize: 12,
                fontStyle: 'italic',
                color: incompleto ? '#7c2d12' : '#7a8896',
                ml: span ? 0.5 : 0,
            }}
        >
            {ressalva}
        </Typography>
    )
}

// EVIDÊNCIA CLICÁVEL do PNCP: o número "N contratações no PNCP" e o selo
// "Y por inexigibilidade" abrem um painel (modal) com a LISTA real por trás dos
// números — órgão, objeto, modalidade, valor (quando houver) e link p/ o portal
// PNCP —, com um resumo (total + quebra por modalidade) no topo. FONTE ÚNICA:
// os números do painel são exatamente os já exibidos no card (a mesma coleta do
// enriquecimento; a busca aberta não traz preço → valor pode ser null).
// Acessível por teclado (Dialog do MUI: foco preso, Esc fecha); os gatilhos são
// <button> reais. Modal (não expansão) para não conflitar com o clique do
// checkbox/radio do card nem quebrar o layout denso das linhas.
const EvidenciaPncp = ({s}: {s: SolucaoMercado}) => {
    const [aberto, setAberto] = useState(false)
    const total = s.contratacoes_pncp ?? 0
    if (total <= 0) return null

    const {numero, ressalva} = rotuloContagem(s)
    const inex = s.modalidades_pncp?.inexigibilidade ?? 0
    const totalMod = s.modalidades_pncp?.total ?? total
    const exemplos: ContratacaoPncp[] = s.contratacoes_exemplos ?? []
    const nomesModalidade = Object.entries(s.modalidades_pncp_nomes ?? {}).sort(
        (a, b) => b[1] - a[1]
    )
    // Fallback p/ dados enriquecidos ANTES desta feature (só links, sem a
    // lista): o painel ainda abre e leva ao PNCP, nunca vazio.
    const links = s.contratacoes_links ?? []

    const rotulo = `${numero} contratação(ões) no PNCP citando este fornecedor`

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1,
                    mt: 0.25,
                }}
            >
                <Button
                    onClick={() => setAberto(true)}
                    startIcon={<TravelExploreOutlinedIcon sx={{fontSize: 16}} />}
                    aria-label={`Ver a evidência: ${rotulo}${
                        ressalva ? ` — ${ressalva}` : ''
                    }`}
                    aria-haspopup="dialog"
                    sx={{
                        p: 0,
                        minWidth: 0,
                        textTransform: 'none',
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#14532d',
                        textDecoration: 'underline',
                        textUnderlineOffset: 2,
                        '&:hover': {
                            background: 'transparent',
                            textDecoration: 'underline',
                        },
                    }}
                >
                    {rotulo}
                </Button>
                {/* MESMA ressalva do painel: o card não pode mostrar um número
                    "fechado" quando a coleta foi truncada. */}
                <RessalvaContagem s={s} />
                {inex > 0 && (
                    <Button
                        onClick={() => setAberto(true)}
                        aria-label={`Ver a evidência: ${inex} de ${totalMod} por inexigibilidade`}
                        aria-haspopup="dialog"
                        sx={{
                            p: 0,
                            minWidth: 0,
                            textTransform: 'none',
                            fontSize: 12,
                            fontWeight: 600,
                            color: '#7c2d12',
                            textDecoration: 'underline',
                            textUnderlineOffset: 2,
                            '&:hover': {
                                background: 'transparent',
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        {inex} de {totalMod} por inexigibilidade
                    </Button>
                )}
            </Box>

            <Dialog
                open={aberto}
                onClose={() => setAberto(false)}
                maxWidth="sm"
                fullWidth
                aria-labelledby="evidencia-pncp-titulo"
            >
                <DialogTitle
                    id="evidencia-pncp-titulo"
                    sx={{fontSize: 16, fontWeight: 700, pr: 5}}
                >
                    Contratações no PNCP — {s.nome_solucao}
                    <IconButton
                        aria-label="Fechar"
                        onClick={() => setAberto(false)}
                        sx={{position: 'absolute', right: 8, top: 8}}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {/* Resumo: total + quebra por modalidade (fonte única). */}
                    <Typography sx={{fontSize: 13, color: '#1f2b3a', mb: 0.5}}>
                        <strong>{numero}</strong> contratação(ões) no PNCP citam
                        este fornecedor.
                        <RessalvaContagem s={s} span />
                    </Typography>
                    {inex > 0 && (
                        <Typography
                            sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: '#7c2d12',
                                mb: 1,
                            }}
                        >
                            {inex} de {totalMod} por inexigibilidade — indício, a
                            confirmar pelo gestor.
                        </Typography>
                    )}
                    {nomesModalidade.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                                mb: 1.5,
                            }}
                        >
                            {nomesModalidade.map(([nome, qtd]) => (
                                <Chip
                                    key={nome}
                                    size="small"
                                    label={`${nome}: ${qtd}`}
                                    color={
                                        ehInexigibilidade(nome)
                                            ? 'warning'
                                            : 'default'
                                    }
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    )}

                    {exemplos.length > 0
                        ? exemplos.map((c, i) => (
                              <SolucaoRow key={`${c.url}-${i}`}>
                                  <Box
                                      sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'flex-start',
                                          gap: 1,
                                      }}
                                  >
                                      <Box sx={{minWidth: 0}}>
                                          {c.orgao && (
                                              <Typography
                                                  sx={{
                                                      fontSize: 13,
                                                      fontWeight: 700,
                                                      color: '#1f2b3a',
                                                  }}
                                              >
                                                  {c.orgao}
                                              </Typography>
                                          )}
                                          <Typography
                                              sx={{
                                                  fontSize: 13,
                                                  color: '#4a5563',
                                              }}
                                          >
                                              {c.titulo}
                                          </Typography>
                                          <Box
                                              sx={{
                                                  display: 'flex',
                                                  flexWrap: 'wrap',
                                                  alignItems: 'center',
                                                  gap: 0.5,
                                                  mt: 0.5,
                                              }}
                                          >
                                              {c.modalidade && (
                                                  <Chip
                                                      size="small"
                                                      label={c.modalidade}
                                                      color={
                                                          ehInexigibilidade(
                                                              c.modalidade
                                                          )
                                                              ? 'warning'
                                                              : 'default'
                                                      }
                                                      variant="outlined"
                                                  />
                                              )}
                                          </Box>
                                      </Box>
                                      <Box
                                          sx={{
                                              flexShrink: 0,
                                              display: 'flex',
                                              flexDirection: 'column',
                                              alignItems: 'flex-end',
                                              gap: 0.25,
                                          }}
                                      >
                                          {/* Valor global do contrato (PNCP):
                                              R$ pt-BR; "—" quando o PNCP não
                                              informa (honesto, igual às demais
                                              telas). */}
                                          <Typography
                                              sx={{
                                                  fontSize: 12,
                                                  fontWeight: 700,
                                                  whiteSpace: 'nowrap',
                                                  color:
                                                      c.valor != null
                                                          ? '#14532d'
                                                          : '#7a8896',
                                              }}
                                          >
                                              {currency(c.valor)}
                                          </Typography>
                                          {c.url && (
                                              <Button
                                                  size="small"
                                                  component="a"
                                                  href={c.url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  endIcon={
                                                      <OpenInNewIcon
                                                          sx={{fontSize: 14}}
                                                      />
                                                  }
                                                  sx={{
                                                      textTransform: 'none',
                                                      fontSize: 12,
                                                      whiteSpace: 'nowrap',
                                                  }}
                                              >
                                                  abrir no PNCP
                                              </Button>
                                          )}
                                      </Box>
                                  </Box>
                              </SolucaoRow>
                          ))
                        : links.map((u, i) => (
                              <SolucaoRow key={`${u}-${i}`}>
                                  <Button
                                      size="small"
                                      component="a"
                                      href={u}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      endIcon={
                                          <OpenInNewIcon sx={{fontSize: 14}} />
                                      }
                                      sx={{
                                          textTransform: 'none',
                                          fontSize: 12,
                                      }}
                                  >
                                      abrir contratação {i + 1} no PNCP
                                  </Button>
                              </SolucaoRow>
                          ))}
                    <Typography
                        sx={{fontSize: 11, color: '#7a8896', mt: 1}}
                    >
                        Evidência coletada pela Licito.guru IA na base pública do
                        PNCP. A busca aberta não traz o preço homologado.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAberto(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

// Fluxo NOVO quando o conceito foi escolhido, o status é de um passo novo, ou
// o levantamento ainda não investigou nada (nada a perder — entra no novo).
// Antigos em INVESTIGANDO/COMPARANDO/CONCLUIDO sem conceito → fluxo antigo.
const ehFluxoNovo = (d: LevantamentoDetail): boolean =>
    !!d.conceito_escolhido_id ||
    ['CONCEITOS_PRONTOS', 'SOLUCOES_PRONTAS', 'PARECER_PRONTO'].includes(
        d.status
    ) ||
    ['RASCUNHO', 'CONVERSANDO', 'ALTERNATIVAS_PRONTAS'].includes(d.status)

// Passo sugerido ao abrir um levantamento existente, a partir do status.
const stepDoStatus = (d: LevantamentoDetail): number => {
    if (d.status === 'CONCLUIDO') return 4
    if (d.status === 'COMPARANDO') return d.justificativa_final ? 3 : 2
    if (d.status === 'INVESTIGANDO') return 2
    if (d.status === 'ALTERNATIVAS_PRONTAS') return 1
    return 0
}

// Passo sugerido no fluxo NOVO (6 passos).
const stepDoStatusNovo = (d: LevantamentoDetail): number => {
    if (d.status === 'CONCLUIDO') return 5
    if (d.solucao_preferida || d.justificativa_final) return 4
    if (d.status === 'PARECER_PRONTO') return 3
    if (d.status === 'SOLUCOES_PRONTAS') {
        const conceito = d.alternativas.find(
            (a) => a.id === d.conceito_escolhido_id
        )
        const algumaMarcada = (conceito?.solucoes?.itens ?? []).some(
            (s) => s.selecionada
        )
        return algumaMarcada ? 3 : 2
    }
    if (d.status === 'INVESTIGANDO') return 2
    if (d.status === 'ALTERNATIVAS_PRONTAS' || d.status === 'CONCEITOS_PRONTOS')
        return 1
    return 0
}

const CONTEXTO_CAMPOS: {
    key: 'orgao' | 'esfera' | 'volume' | 'prazo' | 'orcamento' | 'restricoes'
    label: string
    placeholder: string
}[] = [
    {key: 'orgao', label: 'Órgão', placeholder: 'Ex.: Prefeitura de …'},
    {
        key: 'esfera',
        label: 'Esfera',
        placeholder: 'Ex.: municipal, estadual, federal',
    },
    {
        key: 'volume',
        label: 'Volume estimado da demanda',
        placeholder: 'Ex.: 120 mil páginas, 40 usuários…',
    },
    {key: 'prazo', label: 'Prazo', placeholder: 'Ex.: implantação em 6 meses'},
    {
        key: 'orcamento',
        label: 'Orçamento disponível (se houver)',
        placeholder: 'Ex.: até R$ 300.000,00',
    },
    {
        key: 'restricoes',
        label: 'Restrições',
        placeholder: 'Ex.: sem obra civil, equipe reduzida…',
    },
]

export default function Levantamento() {
    const api = useMemo(() => new LevantamentoApiService(), [])
    const navigate = useNavigate()

    const [view, setView] = useState<View>('list')
    const [lista, setLista] = useState<LevantamentoResumo[]>([])
    const [listaLoading, setListaLoading] = useState(true)
    const [detail, setDetail] = useState<LevantamentoDetail | null>(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const [activeStep, setActiveStep] = useState(0)
    // Etapa MÁXIMA já alcançada (monotônica dentro do mesmo levantamento):
    // habilita a navegação livre entre TODAS as etapas já atingidas — voltar E
    // reavançar — sem reprocessar nada. Sobe pelo avanço manual (activeStep) e
    // pelo status (push da IA); zera só ao abrir outro levantamento.
    const [maxStepAlcancado, setMaxStepAlcancado] = useState(0)
    const [erro, setErro] = useState<string | null>(null)
    // Id do levantamento ATIVO na tela: respostas em voo de outro levantamento
    // (ou de antes de voltar à lista) são descartadas em aplicarDetalhe.
    const activeIdRef = useRef<string | null>(null)

    // Passo 1 — problema + contexto + conversa
    const [problema, setProblema] = useState('')
    const [contexto, setContexto] = useState<Record<string, string>>({})
    const [criando, setCriando] = useState(false)
    const [msgInput, setMsgInput] = useState('')
    const [enviandoMsg, setEnviandoMsg] = useState(false)
    const chatRef = useRef<HTMLDivElement | null>(null)

    // Passo 2 — dialog de alternativa (adicionar/editar)
    const [altDialogOpen, setAltDialogOpen] = useState(false)
    const [altEditando, setAltEditando] = useState<AlternativaOut | null>(null)
    const [altForm, setAltForm] = useState<AlternativaCreate>({
        titulo: '',
        descricao: '',
        tipo: 'COMPRA',
    })
    const [altSalvando, setAltSalvando] = useState(false)
    const [investigandoReq, setInvestigandoReq] = useState(false)

    // Passo 4 — escolha + justificativa
    const [escolhaId, setEscolhaId] = useState('')
    const [observacoes, setObservacoes] = useState('')
    const [gerandoJustificativa, setGerandoJustificativa] = useState(false)
    const [justificativa, setJustificativa] = useState('')
    const [salvandoJust, setSalvandoJust] = useState(false)

    // Fluxo novo — escolha de conceito, seleção de soluções e escolha final
    const [escolhendoConceitoId, setEscolhendoConceitoId] = useState<string | null>(null)
    const [selecaoNomes, setSelecaoNomes] = useState<Record<string, boolean>>({})
    const [manuais, setManuais] = useState<{nome_solucao: string; site?: string}[]>([])
    const [manualNome, setManualNome] = useState('')
    const [manualSite, setManualSite] = useState('')
    const [selecionando, setSelecionando] = useState(false)
    const [solucaoEscolhida, setSolucaoEscolhida] = useState('')
    const [obsSolucao, setObsSolucao] = useState('')
    const [escolhendoSolucao, setEscolhendoSolucao] = useState(false)

    // Diferenciais supervisionados (edição local sobre os sugeridos da IA).
    const [difEdicao, setDifEdicao] = useState<
        {texto: string; base: string; origem: string; incluido: boolean}[]
    >([])
    const [difNovoTexto, setDifNovoTexto] = useState('')
    const [difNovaBase, setDifNovaBase] = useState('')
    const [confirmandoDif, setConfirmandoDif] = useState(false)
    // Diagnóstico de EVIDÊNCIA DE SITE no passo de diferenciais (caso real:
    // site da escolhida ilegível → análise vazia SEM pista): edição local do
    // site oficial por solução + envio "ler site e refazer" em voo.
    const [siteCorrigido, setSiteCorrigido] = useState<Record<string, string>>(
        {}
    )
    const [lendoSiteDe, setLendoSiteDe] = useState<string | null>(null)
    // Requisitos técnicos essenciais (mesmo padrão supervisionado).
    const [reqEdicao, setReqEdicao] = useState<
        {
            texto: string
            origem: string
            incluido: boolean
            recomendado?: boolean
            justificativa?: string
        }[]
    >([])
    const [reqNovoTexto, setReqNovoTexto] = useState('')
    const [confirmandoReq, setConfirmandoReq] = useState(false)
    const [iniciandoRequisitos, setIniciandoRequisitos] = useState(false)
    // Pedido de informações ao mercado (RFI).
    const [rfiDialogAberto, setRfiDialogAberto] = useState(false)
    const [rfiDestNome, setRfiDestNome] = useState('')
    const [rfiGerando, setRfiGerando] = useState(false)
    const [rfiEdicao, setRfiEdicao] = useState<{id: string; texto: string} | null>(
        null
    )
    const [rfiSalvando, setRfiSalvando] = useState(false)
    // Export do RFI em voo, por RFI+formato (chave `${id}:${formato}`) — trava
    // o clique duplo (cada export é uma chamada assíncrona ao backend).
    const [exportandoRfi, setExportandoRfi] = useState<Record<string, boolean>>(
        {}
    )

    // Passo 5 — relatório
    const [concluindo, setConcluindo] = useState(false)
    const [relatorioHtml, setRelatorioHtml] = useState<string | null>(null)
    const [relatorioLoading, setRelatorioLoading] = useState(false)
    const [relatorioErro, setRelatorioErro] = useState(false)
    // Controle de retry fora do ciclo de render (o effect não pode depender do
    // próprio loading — era isso que gerava o loop infinito de requisições).
    const relatorioBuscaRef = useRef({emVoo: false, tentativa: 0})

    const carregarLista = useCallback(async () => {
        setListaLoading(true)
        try {
            const data = await api.listLevantamentos()
            setLista(data)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setListaLoading(false)
        }
    }, [api])

    useEffect(() => {
        carregarLista()
    }, [carregarLista])

    // Mantém a etapa máxima alcançada monotônica: o maior entre a etapa atual
    // (avanço manual) e a etapa derivada do status (avanço por push da IA).
    // Nunca desce — voltar no stepper não “esquece” etapas já atingidas.
    useEffect(() => {
        if (!detail) return
        const statusStep = ehFluxoNovo(detail)
            ? stepDoStatusNovo(detail)
            : stepDoStatus(detail)
        setMaxStepAlcancado((prev) => Math.max(prev, activeStep, statusStep))
    }, [detail, activeStep])

    // Aplica um detalhe recém-buscado sem sobrescrever o que o usuário digitou.
    const aplicarDetalhe = useCallback((d: LevantamentoDetail) => {
        // Resposta obsoleta (usuário voltou à lista ou abriu OUTRO levantamento
        // enquanto o GET estava em voo) → descarta: sem isso o detail
        // ressuscitava e o polling continuava rodando oculto.
        if (activeIdRef.current !== d.id) return
        setDetail(d)
        // Dedup dos balões otimistas: quando o backend já devolve a MESMA
        // mensagem do usuário (mesmo papel + conteúdo), descarta o otimista para
        // não duplicar o balão no chat.
        setMsgsOtimistas((prev) =>
            prev.filter(
                (mo) =>
                    !d.mensagens.some(
                        (m) =>
                            m.papel === mo.papel &&
                            m.conteudo.trim() === mo.conteudo.trim()
                    )
            )
        )
        // Avança sozinho quando a IA propõe o mapa de alternativas/conceitos.
        setActiveStep((prev) =>
            (d.status === 'ALTERNATIVAS_PRONTAS' ||
                d.status === 'CONCEITOS_PRONTOS') &&
            prev === 0
                ? 1
                : prev
        )
        if (d.justificativa_final) {
            setGerandoJustificativa(false)
            // Não sobrescreve edição em andamento do usuário.
            setJustificativa((j) => j || d.justificativa_final || '')
        } else {
            // A IA falhou ao redigir (degradação graciosa do backend vira
            // mensagem SISTEMA na etapa COMPARACAO): para o spinner e o polling
            // em vez de girar para sempre.
            const ultima = d.mensagens[d.mensagens.length - 1]
            if (ultima?.papel === 'SISTEMA' && ultima?.etapa === 'COMPARACAO') {
                setGerandoJustificativa(false)
            }
        }
        setEscolhaId((prev) => prev || d.solucao_escolhida_id || '')
    }, [])

    const abrirLevantamento = useCallback(
        async (id: string) => {
            activeIdRef.current = id
            setDetailLoading(true)
            setView('detail')
            setRelatorioHtml(null)
            setRelatorioErro(false)
            relatorioBuscaRef.current = {emVoo: false, tentativa: 0}
            setJustificativa('')
            setEscolhaId('')
            setObservacoes('')
            setGerandoJustificativa(false)
            setRespostaCustom('')
            setMostrarCampoCustom(false)
            setDetalheFinal('')
            setConversaFechadaId(null)
            setMsgsOtimistas([])
            setMaxStepAlcancado(0)
            setSelecaoNomes({})
            setManuais([])
            setManualNome('')
            setManualSite('')
            setSolucaoEscolhida('')
            setObsSolucao('')
            setEscolhendoConceitoId(null)
            try {
                const d = await api.getLevantamento(id)
                if (activeIdRef.current !== id) return
                // Popula os campos do passo 1 com o dado REAL do levantamento —
                // nunca fica vazio nem exibe rascunho de outro fluxo.
                setProblema(d.problema_texto || '')
                const ctx: Record<string, string> = {}
                for (const [k, v] of Object.entries(d.contexto ?? {})) {
                    if (typeof v === 'string' && v) ctx[k] = v
                }
                setContexto(ctx)
                aplicarDetalhe(d)
                setActiveStep(
                    ehFluxoNovo(d) ? stepDoStatusNovo(d) : stepDoStatus(d)
                )
            } catch (e) {
                if (activeIdRef.current !== id) return
                setErro(msgDeErro(e))
                setView('list')
            } finally {
                setDetailLoading(false)
            }
        },
        [api, aplicarDetalhe]
    )

    // Progresso em tempo real: PUSH via user-notifier → websocket do app →
    // HistoryContext (demux por module === 'levantamento-mercado'). Cada push
    // do levantamento aberto dispara UM refetch pontual do detalhe — nunca loop.
    const {levantamentoSocketMessage} = useContext(HistoryContext)
    useEffect(() => {
        const msg = levantamentoSocketMessage
        if (!msg?.levantamento_id) return
        if (msg.levantamento_id !== activeIdRef.current) return
        api.getLevantamento(msg.levantamento_id)
            .then(aplicarDetalhe) // respostas obsoletas são descartadas lá
            .catch(() => {
                // silencioso — o botão Atualizar é a rede de segurança
            })
    }, [api, aplicarDetalhe, levantamentoSocketMessage])

    const emAndamento =
        !!detail &&
        (detail.status === 'CONVERSANDO' ||
            detail.status === 'INVESTIGANDO' ||
            gerandoJustificativa)

    // Fallback SÓ de dev local (ambiente sem user-notifier de pé): poll lento
    // gated por VITE_LEVANTAMENTO_POLL === 'true'. Default OFF — nunca em prod.
    useEffect(() => {
        if (import.meta.env.VITE_LEVANTAMENTO_POLL !== 'true') return
        if (!emAndamento || !detail?.id) return
        const id = detail.id
        let emVoo = false // não empilha ticks em rede lenta
        const t = setInterval(async () => {
            if (emVoo) return
            emVoo = true
            try {
                const d = await api.getLevantamento(id)
                aplicarDetalhe(d)
            } catch {
                // silencioso — o próximo tick tenta de novo
            } finally {
                emVoo = false
            }
        }, DEV_POLL_INTERVAL_MS)
        return () => clearInterval(t)
    }, [api, aplicarDetalhe, detail?.id, emAndamento])

    // Rede de segurança manual (push perdido/reconexão do WS): 1 GET por clique.
    const [atualizando, setAtualizando] = useState(false)
    const atualizarDetalhe = async () => {
        if (!detail?.id) return
        setAtualizando(true)
        // Atualização manual também re-lê as pesquisas IN 65 vinculadas
        // (limpa o cache — inclusive status terminais).
        in65BuscadasRef.current.clear()
        try {
            const d = await api.getLevantamento(detail.id)
            aplicarDetalhe(d)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setAtualizando(false)
        }
    }

    // Mantém a conversa rolada para a última mensagem.
    useEffect(() => {
        if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight
        }
    }, [detail?.mensagens?.length])

    // Carrega o relatório quando o passo 5 abre com o levantamento concluído.
    // Tentativas limitadas com backoff DENTRO do mesmo ciclo (via ref/timeout):
    // depender do loading no array de deps re-disparava o effect a cada falha e
    // martelava o backend em loop infinito.
    useEffect(() => {
        // Passo do relatório: 5 no fluxo novo (6 passos), 4 no antigo.
        const stepRelatorio = detail && ehFluxoNovo(detail) ? 5 : 4
        if (
            activeStep !== stepRelatorio ||
            !detail?.id ||
            detail.status !== 'CONCLUIDO' ||
            relatorioHtml !== null ||
            relatorioErro ||
            relatorioBuscaRef.current.emVoo
        )
            return
        const alvoId = detail.id
        relatorioBuscaRef.current.emVoo = true
        setRelatorioLoading(true)
        let timer: ReturnType<typeof setTimeout> | undefined
        const buscar = () => {
            api.getRelatorioHtml(alvoId)
                .then((html) => {
                    if (activeIdRef.current !== alvoId) {
                        relatorioBuscaRef.current.emVoo = false
                        return
                    }
                    relatorioBuscaRef.current = {emVoo: false, tentativa: 0}
                    setRelatorioHtml(html)
                    setRelatorioLoading(false)
                })
                .catch((e) => {
                    if (activeIdRef.current !== alvoId) {
                        relatorioBuscaRef.current.emVoo = false
                        return
                    }
                    const tentativa = relatorioBuscaRef.current.tentativa + 1
                    relatorioBuscaRef.current.tentativa = tentativa
                    if (tentativa >= RELATORIO_MAX_TENTATIVAS) {
                        // Estado terminal: para de buscar até o usuário pedir de novo.
                        relatorioBuscaRef.current.emVoo = false
                        setRelatorioErro(true)
                        setRelatorioLoading(false)
                        setErro(msgDeErro(e))
                        return
                    }
                    timer = setTimeout(
                        buscar,
                        RELATORIO_BACKOFF_MS * 2 ** (tentativa - 1)
                    )
                })
        }
        buscar()
        return () => {
            if (timer) {
                clearTimeout(timer)
                relatorioBuscaRef.current.emVoo = false
            }
        }
    }, [activeStep, api, detail?.id, detail?.status, relatorioHtml, relatorioErro])

    const tentarRelatorioDeNovo = () => {
        relatorioBuscaRef.current = {emVoo: false, tentativa: 0}
        setRelatorioErro(false) // o effect volta a buscar
    }

    const novoLevantamento = () => {
        activeIdRef.current = null
        setDetail(null)
        setProblema('')
        setContexto({})
        setMsgInput('')
        setJustificativa('')
        setEscolhaId('')
        setObservacoes('')
        setGerandoJustificativa(false)
        setRespostaCustom('')
        setMostrarCampoCustom(false)
        setDetalheFinal('')
        setConversaFechadaId(null)
        setRelatorioHtml(null)
        setRelatorioErro(false)
        relatorioBuscaRef.current = {emVoo: false, tentativa: 0}
        setActiveStep(0)
        setView('detail')
    }

    const criarLevantamento = async () => {
        if (problema.trim().length < 10) {
            setErro('Descreva o problema com um pouco mais de detalhe (mínimo de 10 caracteres).')
            return
        }
        setCriando(true)
        let criadoId: string | null = null
        try {
            const ctx: Record<string, string> = {}
            for (const [k, v] of Object.entries(contexto)) {
                if (v && v.trim()) ctx[k] = v.trim()
            }
            const out = await api.createLevantamento({
                problema_texto: problema.trim(),
                contexto: Object.keys(ctx).length ? ctx : undefined,
            })
            criadoId = out.id
            activeIdRef.current = out.id
            const d = await api.getLevantamento(out.id)
            aplicarDetalhe(d)
        } catch (e) {
            if (criadoId) {
                // O POST criou o levantamento; só o GET seguinte falhou. NUNCA
                // volta ao estado "não criado" (o botão reabilitado induzia um
                // segundo POST = levantamento duplicado + IA disparada 2x).
                // Esqueleto local + polling recuperam o detalhe sozinhos.
                setDetail({
                    id: criadoId,
                    status: 'CONVERSANDO',
                    problema_texto: problema.trim(),
                    contexto: {},
                    mensagens: [],
                    alternativas: [],
                    solucao_escolhida_id: null,
                    justificativa_final: '',
                    resultado: null,
                })
            } else {
                setErro(msgDeErro(e))
            }
        } finally {
            setCriando(false)
        }
    }

    const enviarMensagem = async () => {
        if (!detail?.id || !msgInput.trim()) return
        setEnviandoMsg(true)
        try {
            await api.enviarMensagem(detail.id, msgInput.trim())
            setMsgInput('')
            const d = await api.getLevantamento(detail.id)
            aplicarDetalhe(d)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setEnviandoMsg(false)
        }
    }

    // ---- perguntas de contexto estruturadas (uma por vez, sugestões clicáveis)
    const [respostaCustom, setRespostaCustom] = useState('')
    const [mostrarCampoCustom, setMostrarCampoCustom] = useState(false)
    const [respondendo, setRespondendo] = useState(false)
    // Conversa FINITA: após o roteiro de perguntas, uma pergunta final de fecho
    // (opcional) e então o input FECHA — a conversa deixa de parecer chat
    // perpétuo. detalheFinal = texto do campo de fecho; conversaFechadaId marca
    // o levantamento cujo fecho o usuário já concluiu (por id, para não vazar
    // entre levantamentos).
    const [detalheFinal, setDetalheFinal] = useState('')
    const [conversaFechadaId, setConversaFechadaId] = useState<string | null>(
        null
    )
    // Balões OTIMISTAS do fecho da conversa: o que o usuário acabou de enviar
    // aparece na hora, sem esperar o backend materializar / o refetch chegar. O
    // dedup no aplicarDetalhe remove cada um quando a MESMA mensagem retorna do
    // backend (por papel + conteúdo).
    const [msgsOtimistas, setMsgsOtimistas] = useState<MensagemOut[]>([])

    const perguntasContexto = detail?.perguntas ?? []
    const idxPerguntaCorrente = perguntasContexto.findIndex((p) => !p.resposta)
    const perguntaCorrente =
        idxPerguntaCorrente >= 0 ? perguntasContexto[idxPerguntaCorrente] : null
    const numRespondidas = perguntasContexto.filter((p) => !!p.resposta).length
    // Roteiro estruturado terminou (havia perguntas e todas respondidas): é a
    // hora da pergunta final de fecho.
    const roteiroConcluido =
        perguntasContexto.length > 0 && !perguntaCorrente
    const conversaFechada = !!detail && conversaFechadaId === detail.id

    // Fecho da conversa: envia o detalhe final (se houver) como mais uma
    // mensagem de contexto — mesmo canal do input livre de hoje — e FECHA o
    // input. A transição de etapa segue a existente (push do backend quando os
    // conceitos ficam prontos avança o passo sozinho).
    const concluirConversa = async (texto?: string) => {
        if (!detail?.id) return
        const alvo = detail.id
        const t = (texto ?? '').trim()
        if (t) {
            // Append OTIMISTA do balão do usuário: aparece imediatamente na
            // lista visível do chat, independente do refetch (que pode chegar
            // antes de o backend materializar a mensagem). Dedup no
            // aplicarDetalhe quando a mesma mensagem retornar do backend.
            const otimista: MensagemOut = {
                papel: 'USUARIO',
                conteudo: t,
                etapa: 'CONTEXTO',
                created_at: new Date().toISOString(),
            }
            setMsgsOtimistas((prev) => [...prev, otimista])
            setEnviandoMsg(true)
            try {
                await api.enviarMensagem(alvo, t)
                const d = await api.getLevantamento(alvo)
                aplicarDetalhe(d)
            } catch (e) {
                // Envio falhou (não persistiu): remove o balão otimista e mantém
                // a conversa aberta para nova tentativa.
                setMsgsOtimistas((prev) => prev.filter((m) => m !== otimista))
                setErro(msgDeErro(e))
                setEnviandoMsg(false)
                return // erro: não fecha, permite tentar de novo
            }
            setEnviandoMsg(false)
        }
        setDetalheFinal('')
        setConversaFechadaId(alvo)
    }

    const responderPerguntaContexto = async (indice: number, resposta: string) => {
        if (!detail?.id || respondendo) return
        const texto = resposta.trim()
        if (!texto) return
        if (texto.length > 500) {
            setErro('A resposta deve ter no máximo 500 caracteres.')
            return
        }
        setRespondendo(true)
        try {
            await api.responderPergunta(detail.id, indice, texto)
            setRespostaCustom('')
            setMostrarCampoCustom(false)
            // Refetch pontual: mostra a próxima pergunta (ou aguarda o mapa
            // chegar por push quando esta era a última) — sem loop.
            const d = await api.getLevantamento(detail.id)
            aplicarDetalhe(d)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setRespondendo(false)
        }
    }

    const abrirDialogAlternativa = (alt?: AlternativaOut) => {
        setAltEditando(alt ?? null)
        setAltForm(
            alt
                ? {titulo: alt.titulo, descricao: alt.descricao, tipo: alt.tipo}
                : {titulo: '', descricao: '', tipo: 'COMPRA'}
        )
        setAltDialogOpen(true)
    }

    const salvarAlternativa = async () => {
        if (!detail?.id) return
        if (!altForm.titulo.trim() || !altForm.descricao.trim()) {
            setErro('Preencha título e descrição da alternativa.')
            return
        }
        setAltSalvando(true)
        try {
            if (altEditando) {
                await api.editAlternativa(detail.id, altEditando.id, altForm)
            } else {
                await api.addAlternativa(detail.id, altForm)
            }
            const d = await api.getLevantamento(detail.id)
            aplicarDetalhe(d)
            setAltDialogOpen(false)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setAltSalvando(false)
        }
    }

    const removerAlternativa = async (alt: AlternativaOut) => {
        if (!detail?.id) return
        try {
            await api.deleteAlternativa(detail.id, alt.id)
            const d = await api.getLevantamento(detail.id)
            aplicarDetalhe(d)
        } catch (e) {
            setErro(msgDeErro(e))
        }
    }

    const investigar = async () => {
        if (!detail?.id) return
        setInvestigandoReq(true)
        try {
            await api.investigar(detail.id)
            const d = await api.getLevantamento(detail.id)
            aplicarDetalhe(d)
            setActiveStep(2)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setInvestigandoReq(false)
        }
    }

    const gerarJustificativa = async () => {
        if (!detail?.id || !escolhaId) {
            setErro('Selecione a alternativa escolhida antes de gerar a justificativa.')
            return
        }
        setGerandoJustificativa(true)
        setJustificativa('')
        try {
            await api.escolher(detail.id, {
                alternativa_id: escolhaId,
                observacoes: observacoes.trim() || undefined,
            })
            // Refetch pontual pós-ação (1 fetch); o texto da justificativa
            // chega depois via push do user-notifier (JUSTIFICATIVA_PRONTA).
            const d = await api.getLevantamento(detail.id)
            aplicarDetalhe(d)
        } catch (e) {
            setGerandoJustificativa(false)
            setErro(msgDeErro(e))
        }
    }

    const salvarJustificativa = async () => {
        if (!detail?.id || !justificativa.trim()) return
        setSalvandoJust(true)
        try {
            await api.salvarJustificativa(detail.id, justificativa.trim())
            const d = await api.getLevantamento(detail.id)
            setDetail(d)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setSalvandoJust(false)
        }
    }

    const concluir = async () => {
        if (!detail?.id) return
        setConcluindo(true)
        try {
            await api.concluir(detail.id)
            const d = await api.getLevantamento(detail.id)
            aplicarDetalhe(d)
            setRelatorioHtml(null)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setConcluindo(false)
        }
    }

    // Handoff manual para a Pesquisa de Preços — POR ALTERNATIVA: o usuário
    // pode pesquisar preços (IN 65) de QUALQUER alternativa, inclusive durante
    // a investigação/escolha (o preço é insumo para decidir). A página destino
    // pré-preenche a descrição e, após criar, vincula a pesquisa de volta à
    // alternativa (POST /vincular-pesquisa, best-effort de lá).
    const pesquisarPrecosDaAlternativa = (alt: AlternativaOut) => {
        navigate('/pesquisa-precos', {
            state: {
                origem: 'levantamento',
                levantamento_id: detail?.id,
                alternativa_id: alt.id,
                descricao_solucao:
                    `${alt.titulo}${alt.descricao ? ` — ${alt.descricao}` : ''}`,
            },
        })
    }

    // Status/valor das pesquisas IN 65 vinculadas — best-effort via
    // PriceResearchApi (import dinâmico); sem os dados, o chip mostra só o vínculo.
    // Cache por id com INVALIDAÇÃO: só fixa no cache status TERMINAL
    // (CONCLUIDO/ERRO); pesquisa em andamento é rebuscada a cada recarga do
    // detalhe (push do notifier, refetch pós-ação ou botão Atualizar) — sem polling.
    const [pesquisasIn65, setPesquisasIn65] = useState<
        Record<string, {status?: string; valor_total_estimado?: number | null}>
    >({})
    const in65BuscadasRef = useRef<Set<string>>(new Set())
    useEffect(() => {
        const pendentes = (detail?.alternativas ?? [])
            .map((a) => a.pesquisa_precos_id)
            .filter((pid): pid is string => !!pid && !in65BuscadasRef.current.has(pid))
        if (pendentes.length === 0) return
        pendentes.forEach((pid) => in65BuscadasRef.current.add(pid))
        let cancelado = false
        import('../../../services/PriceResearchApi.service')
            .then(async ({PriceResearchApiService}) => {
                const apiPr = new PriceResearchApiService()
                for (const pid of pendentes) {
                    try {
                        const p = await apiPr.getPesquisa(pid)
                        if (cancelado) return
                        if (p.status !== 'CONCLUIDO' && p.status !== 'ERRO') {
                            // Não-terminal: sai do cache p/ a próxima recarga
                            // do detalhe rebuscar (o chip atualiza na tela).
                            in65BuscadasRef.current.delete(pid)
                        }
                        setPesquisasIn65((prev) => ({
                            ...prev,
                            [pid]: {
                                status: p.status,
                                valor_total_estimado: p.valor_total_estimado,
                            },
                        }))
                    } catch {
                        // best-effort — permite nova tentativa na próxima recarga
                        in65BuscadasRef.current.delete(pid)
                    }
                }
            })
            .catch(() => {
                // best-effort
            })
        return () => {
            cancelado = true
        }
    }, [detail?.alternativas])

    const in65Info = (alt: AlternativaOut) =>
        alt.pesquisa_precos_id ? pesquisasIn65[alt.pesquisa_precos_id] : undefined

    // Chip do vínculo IN 65 (com link p/ o módulo de Pesquisa de Preços).
    const renderVinculoIn65 = (alt: AlternativaOut) => {
        if (!alt.pesquisa_precos_id) return null
        const info = in65Info(alt)
        const concluido = info?.status === 'CONCLUIDO'
        const erro = info?.status === 'ERRO'
        // Enquanto a pesquisa não é terminal (ou o status ainda não chegou), a
        // alternativa mostra "em andamento…": a pesquisa foi disparada no
        // handoff e o vínculo sobrevive no backend, então ao voltar ao
        // levantamento o detalhe rebusca e o estado aparece (e depois o valor).
        return (
            <Chip
                size="small"
                variant="outlined"
                color={concluido ? 'success' : erro ? 'warning' : 'info'}
                label={
                    concluido
                        ? 'Pesquisa IN 65 concluída'
                        : erro
                        ? 'Pesquisa IN 65 indisponível'
                        : 'Pesquisa IN 65 em andamento…'
                }
                onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    navigate('/pesquisa-precos')
                }}
            />
        )
    }

    const voltarParaLista = () => {
        activeIdRef.current = null // invalida respostas em voo do detalhe
        setView('list')
        setDetail(null)
        setGerandoJustificativa(false)
        carregarLista()
    }

    // ---- textos finais prontos p/ a Geração de Documentos (copiar/colar) ----
    const textosFinais = (detail?.resultado?.textos_finais ?? null) as {
        descricao_objeto?: string
        trecho_levantamento?: string
        origem?: string
        // Aceno de transparência: redação inspirada em contratações públicas
        // reais da mesma solução (PNCP) — só no caminho de inexigibilidade.
        inspiracao_pncp?: boolean
    } | null
    const [copiado, setCopiado] = useState<'objeto' | 'trecho' | null>(null)
    const copiarTexto = async (chave: 'objeto' | 'trecho', texto: string) => {
        try {
            await navigator.clipboard.writeText(texto)
            setCopiado(chave)
            setTimeout(() => setCopiado((c) => (c === chave ? null : c)), 2000)
        } catch {
            setErro('Não foi possível copiar automaticamente — selecione o texto e copie.')
        }
    }

    const renderCardTextoFinal = (
        chave: 'objeto' | 'trecho',
        titulo: string,
        texto: string
    ) => (
        <Card>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                }}
            >
                <SectionTitle style={{margin: 0}}>{titulo}</SectionTitle>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyIcon fontSize="small" />}
                    onClick={() => copiarTexto(chave, texto)}
                    color={copiado === chave ? 'success' : 'primary'}
                    sx={{textTransform: 'none', fontWeight: 700, borderRadius: '10px'}}
                >
                    {copiado === chave ? 'Copiado!' : 'Copiar'}
                </Button>
            </Box>
            <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={12}
                value={texto}
                InputProps={{readOnly: true}}
                sx={{'& textarea': {fontSize: 13.5, color: '#2a3340'}}}
            />
        </Card>
    )

    const alternativas = detail?.alternativas ?? []
    const alternativasAtivas = alternativas.filter(
        (a) => a.status !== 'DESCARTADA'
    )
    const investigacaoConcluida =
        !!detail &&
        (detail.status === 'COMPARANDO' || detail.status === 'CONCLUIDO')

    // ------------------------- fluxo novo: derivados -------------------------
    const fluxoNovo = !!detail && ehFluxoNovo(detail)
    const conceitoEscolhido = detail?.alternativas.find(
        (a) => a.id === detail?.conceito_escolhido_id
    )
    // Só soluções de MERCADO (contratáveis) entram na seleção/comparação;
    // ferramenta interna de órgão público (contratavel=false) é exibida à
    // parte como referência de tecnologia pública, sem checkbox.
    // Ordem de exibição: a solução que o USUÁRIO nomeou no 1º quadro
    // (origem='solicitada_usuario', injetada pelo backend já selecionada)
    // vem PRIMEIRO; depois as adicionadas à mão (origem='manual'); o resto
    // mantém a ordem do backend (sort estável sobre a cópia do filter).
    const prioridadeOrigem = (origem?: string | null) =>
        origem === 'solicitada_usuario' ? 0 : origem === 'manual' ? 1 : 2
    const itensSolucoes = (conceitoEscolhido?.solucoes?.itens ?? [])
        .filter((s) => s.contratavel !== false)
        .sort((a, b) => prioridadeOrigem(a.origem) - prioridadeOrigem(b.origem))
    const itensNaoContrataveis = (
        conceitoEscolhido?.solucoes?.itens ?? []
    ).filter((s) => s.contratavel === false)
    // Rótulo do chip do cabeçalho. O status do backend marca o PASSO, e
    // SOLUCOES_PRONTAS chega enquanto a descoberta ainda está em voo — o chip
    // então mandava "selecione" uma lista vazia, contradizendo o corpo do passo
    // 3, que na mesma tela diz "ainda está identificando soluções". O usuário lê
    // o chip primeiro e conclui que a busca terminou sem achar nada. Aqui o chip
    // passa a contar a MESMA história do corpo (mesma condição, `itensSolucoes`).
    const rotuloStatus = (d: LevantamentoDetail): string => {
        if (d.status !== 'SOLUCOES_PRONTAS') return STATUS_LABEL[d.status]
        if (conceitoEscolhido?.solucoes?.busca_status === 'FALHOU')
            return 'Busca de soluções indisponível'
        if (itensSolucoes.length === 0)
            return 'Identificando soluções no mercado…'
        return STATUS_LABEL[d.status]
    }
    // Estado do checkbox: edição local do usuário por cima do persistido.
    const marcada = (nome: string, persistida?: boolean) =>
        selecaoNomes[nome] ?? !!persistida
    const selecionadasAtuais = itensSolucoes.filter((s) => s.selecionada)
    const totalMarcadasLocal =
        itensSolucoes.filter((s) => marcada(s.nome_solucao, s.selecionada))
            .length + manuais.length
    const conceitosProntos =
        !!detail &&
        ['SOLUCOES_PRONTAS', 'PARECER_PRONTO', 'CONCLUIDO'].includes(
            detail.status
        )

    // ---------------- pacote da Escolha: derivados -----------------
    const preferida = detail?.solucao_preferida ?? null
    const sinalConceito = conceitoEscolhido?.solucoes?.sinal_conceito ?? null
    const rfis = conceitoEscolhido?.solucoes?.rfis ?? []
    // Escolha específica feita → aguardando os diferenciais da IA (push).
    const aguardandoDiferenciais =
        !!preferida?.nome_solucao &&
        !preferida?.sem_preferencia &&
        preferida?.diferenciais == null
    // Caminho competitivo iniciado → aguardando os requisitos da IA (push).
    const aguardandoRequisitos =
        !!preferida?.sem_preferencia && preferida?.requisitos == null
    // Painéis supervisionados abertos (sugeridos chegaram, sem confirmação).
    const painelDiferenciaisAberto =
        !!preferida?.nome_solucao &&
        !preferida?.sem_preferencia &&
        preferida?.diferenciais != null &&
        preferida?.diferenciais?.confirmados == null
    const painelRequisitosAberto =
        !!preferida?.sem_preferencia &&
        preferida?.requisitos != null &&
        preferida?.requisitos?.confirmados == null
    // Diagnóstico da EVIDÊNCIA DE SITE das soluções comparadas (backend:
    // site_evidencia por item): quando a leitura do site falhou (ex.: site
    // ilegível automaticamente, acesso bloqueado) ou não há site conhecido, a
    // tela de diferenciais explica o porquê e oferece a correção — a falha
    // nunca fica invisível (caso real: análise vazia sem nenhuma pista).
    const solucoesSemLeituraDeSite = itensSolucoes.filter(
        (s) =>
            s.selecionada &&
            (s.site_evidencia?.status === 'falhou' ||
                s.site_evidencia?.status === 'sem_site')
    )

    // Sincroniza a edição local com os SUGERIDOS quando eles chegam (push).
    const difGeradoEm = preferida?.diferenciais?.gerado_em
    useEffect(() => {
        setDifEdicao(
            (preferida?.diferenciais?.sugeridos ?? []).map((d) => ({
                texto: d.texto,
                base: d.base ?? '',
                origem: d.origem ?? 'ia',
                incluido: true,
            }))
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detail?.id, difGeradoEm, painelDiferenciaisAberto])
    const reqGeradoEm = preferida?.requisitos?.gerado_em
    useEffect(() => {
        setReqEdicao(
            (preferida?.requisitos?.sugeridos ?? []).map((r) => ({
                texto: r.texto,
                origem: r.origem ?? 'ia',
                recomendado: r.recomendado ?? true,
                justificativa: r.justificativa ?? '',
                // Recomendados (top-5) pré-marcados; os demais disponíveis e
                // DESMARCADOS — o gestor decide o que exigir. Dado antigo (sem
                // 'recomendado') mantém o comportamento anterior (marcado).
                incluido: r.recomendado ?? true,
            }))
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detail?.id, reqGeradoEm, painelRequisitosAberto])

    // Rede de segurança do push (caso real: soluções prontas no banco e a tela
    // parada em "identificando…"): UM refetch de reconciliação por estado de
    // espera — nunca loop; o push segue sendo o caminho principal.
    const aguardandoAssincrono = ((): string | null => {
        if (!detail) return null
        if (detail.status === 'CONVERSANDO') return 'perguntas'
        if (detail.status === 'INVESTIGANDO') return 'investigacao'
        if (
            fluxoNovo &&
            detail.status === 'SOLUCOES_PRONTAS' &&
            conceitoEscolhido &&
            itensSolucoes.length === 0 &&
            conceitoEscolhido.solucoes?.busca_status !== 'FALHOU'
        )
            return 'solucoes'
        if (aguardandoDiferenciais) return 'diferenciais'
        if (aguardandoRequisitos) return 'requisitos'
        if (gerandoJustificativa) return 'justificativa'
        if (rfis.some((r) => r.status === 'GERANDO')) return 'rfi'
        return null
    })()
    const reconciliadosRef = useRef<Set<string>>(new Set())
    useEffect(() => {
        if (!detail?.id || !aguardandoAssincrono) return
        const chave = `${detail.id}:${aguardandoAssincrono}`
        if (reconciliadosRef.current.has(chave)) return
        const alvoId = detail.id
        const t = setTimeout(() => {
            reconciliadosRef.current.add(chave) // UM único refetch por estado
            api.getLevantamento(alvoId)
                .then(aplicarDetalhe)
                .catch(() => {
                    // silencioso — o botão Atualizar é a rede final
                })
        }, RECONCILIACAO_MS)
        return () => clearTimeout(t)
    }, [aguardandoAssincrono, api, aplicarDetalhe, detail?.id])

    const podeAvancar = (step: number): boolean => {
        if (!detail) return false
        if (fluxoNovo) {
            switch (step) {
                case 0:
                    return alternativas.length > 0
                case 1:
                    return !!detail.conceito_escolhido_id && conceitosProntos
                case 2:
                    return selecionadasAtuais.length > 0
                case 3:
                    // Parecer é insumo, nunca pré-requisito da escolha.
                    return selecionadasAtuais.length > 0
                case 4:
                    return !!detail.justificativa_final
                default:
                    return false
            }
        }
        switch (step) {
            case 0:
                return alternativas.length > 0
            case 1:
                return investigacaoConcluida
            case 2:
                return investigacaoConcluida
            case 3:
                return !!detail.justificativa_final
            default:
                return false
        }
    }

    // ------------------------- fluxo novo: ações -----------------------------
    const escolherConceito = async (alt: AlternativaOut) => {
        if (!detail?.id) return
        setEscolhendoConceitoId(alt.id)
        try {
            await api.escolherConceito(detail.id, alt.id)
            const d = await api.getLevantamento(detail.id)
            aplicarDetalhe(d)
            setSelecaoNomes({})
            setManuais([])
            setSolucaoEscolhida('')
            setActiveStep(2)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setEscolhendoConceitoId(null)
        }
    }

    const adicionarManual = () => {
        const nome = manualNome.trim()
        if (!nome) return
        if (
            manuais.some(
                (m) => m.nome_solucao.toLowerCase() === nome.toLowerCase()
            ) ||
            itensSolucoes.some(
                (s) => s.nome_solucao.toLowerCase() === nome.toLowerCase()
            )
        ) {
            setErro('Essa solução já está na lista.')
            return
        }
        setManuais((prev) => [
            ...prev,
            {nome_solucao: nome, site: manualSite.trim() || undefined},
        ])
        setManualNome('')
        setManualSite('')
    }

    const confirmarSelecao = async () => {
        if (!detail?.id) return
        const selecionadas = itensSolucoes
            .filter((s) => marcada(s.nome_solucao, s.selecionada))
            .map((s) => s.nome_solucao)
        if (selecionadas.length + manuais.length < 1) {
            setErro('Marque ao menos uma solução para a comparação.')
            return
        }
        setSelecionando(true)
        try {
            await api.selecionarSolucoes(detail.id, {selecionadas, manuais})
            setManuais([])
            setSelecaoNomes({})
            const d = await api.getLevantamento(detail.id)
            aplicarDetalhe(d)
            setActiveStep(3)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setSelecionando(false)
        }
    }

    // Retry da pesquisa de soluções (busca_status FALHOU): falha de fonte
    // NUNCA é apresentada como "não há soluções". O estado "repesquisando" é
    // DERIVADO do backend (repesquisa_em, dentro do cooldown) — um refetch
    // qualquer nunca re-habilita o botão com o job em voo; o duplo clique é
    // barrado também no servidor (429 do cooldown, sem custo duplicado).
    const [repesquisandoLocal, setRepesquisandoLocal] = useState(false)
    const repesquisaRecente = (() => {
        const iso = detail?.repesquisa_em
        if (!iso) return false
        const s = /[Zz]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z'
        return Date.now() - new Date(s).getTime() < REPESQUISA_COOLDOWN_MS
    })()
    const repesquisando = repesquisandoLocal || repesquisaRecente
    const repesquisarSolucoes = async () => {
        if (!detail?.id) return
        setRepesquisandoLocal(true)
        try {
            await api.repesquisarSolucoes(detail.id)
            // Refetch traz repesquisa_em do backend — o derivado assume.
            const d = await api.getLevantamento(detail.id)
            aplicarDetalhe(d)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setRepesquisandoLocal(false)
        }
    }

    const escolherSolucaoFinal = async (nome?: string) => {
        const alvo = nome || solucaoEscolhida
        if (!detail?.id || !alvo) return
        setEscolhendoSolucao(true)
        try {
            if (alvo === SEM_PREFERENCIA) {
                // Opção do dono: nenhuma preferida — licitação com requisitos
                // técnicos mínimos (os requisitos da IA chegam por push).
                await api.escolherSolucao(detail.id, {
                    sem_preferencia: true,
                    observacoes: obsSolucao || undefined,
                })
            } else {
                // Escolha específica → DIFERENCIAIS supervisionados antes da
                // justificativa (chegam por push diferenciais_prontos).
                await api.escolherSolucao(detail.id, {
                    nome_solucao: alvo,
                    observacoes: obsSolucao || undefined,
                })
            }
            setJustificativa('')
            const d = await api.getLevantamento(detail.id)
            aplicarDetalhe(d)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setEscolhendoSolucao(false)
        }
    }

    const confirmarDiferenciais = async () => {
        if (!detail?.id) return
        const confirmados: Diferencial[] = difEdicao
            .filter((d) => d.incluido && d.texto.trim())
            .map((d) => ({
                texto: d.texto.trim(),
                base: d.base.trim() || undefined,
                origem: d.origem,
            }))
        if (confirmados.length === 0) {
            setErro(
                'Confirme ao menos um diferencial concreto — ou registre a ' +
                    'escolha sem preferência (licitação com requisitos mínimos).'
            )
            return
        }
        setConfirmandoDif(true)
        try {
            await api.confirmarDiferenciais(detail.id, confirmados)
            setGerandoJustificativa(true)
            setJustificativa('')
            const d = await api.getLevantamento(detail.id)
            aplicarDetalhe(d)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setConfirmandoDif(false)
        }
    }

    // "Ler site e refazer análise": grava o site oficial informado/corrigido,
    // o backend relê o site e refaz os diferenciais (chegam por push — a tela
    // volta ao estado "identificando diferenciais").
    const lerSiteERefazer = async (nome: string, siteAtual?: string) => {
        if (!detail?.id) return
        const site = (siteCorrigido[nome] ?? siteAtual ?? '').trim()
        if (!site) {
            setErro(
                `Informe o site oficial de ${nome} para a leitura ser refeita.`
            )
            return
        }
        setLendoSiteDe(nome)
        try {
            await api.corrigirSiteSolucao(detail.id, {
                nome_solucao: nome,
                site,
            })
            const d = await api.getLevantamento(detail.id)
            aplicarDetalhe(d)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setLendoSiteDe(null)
        }
    }

    const iniciarRequisitos = async () => {
        if (!detail?.id) return
        setIniciandoRequisitos(true)
        try {
            await api.iniciarRequisitosEssenciais(detail.id)
            const d = await api.getLevantamento(detail.id)
            aplicarDetalhe(d)
            setActiveStep(4)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setIniciandoRequisitos(false)
        }
    }

    const confirmarRequisitos = async () => {
        if (!detail?.id) return
        const confirmados: Requisito[] = reqEdicao
            .filter((r) => r.incluido && r.texto.trim())
            .map((r) => ({texto: r.texto.trim(), origem: r.origem}))
        if (confirmados.length === 0) {
            setErro('Confirme ao menos um requisito técnico essencial.')
            return
        }
        setConfirmandoReq(true)
        try {
            await api.confirmarRequisitos(detail.id, confirmados)
            setGerandoJustificativa(true)
            setJustificativa('')
            const d = await api.getLevantamento(detail.id)
            aplicarDetalhe(d)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setConfirmandoReq(false)
        }
    }

    // Handoff Ata Inteligente (conceito de adesão/carona): termo CURTO do OBJETO
    // pré-preenchido via querystring + anotação de rastreabilidade (best-effort,
    // nunca dispara IA).
    // Bug do teste do dono: enviar `termo_busca_pncp` levava "ata registro preços
    // <objeto>" à busca de atas (o instrumento polui e o PNCP dá ZERO). Usa o
    // `termo_ata` (núcleo do objeto, derivado no backend); fail-safe local limpa
    // os tokens de instrumento do termo antigo.
    // Abre em NOVA ABA (window.open) p/ preservar o Levantamento em andamento —
    // antes navegava no lugar e o gestor perdia o que estava fazendo. Os params
    // vão na querystring porque a aba nova não herda o state do react-router.
    const verificarAtas = (alt: AlternativaOut) => {
        const termo =
            alt.termo_ata ||
            limparTermoInstrumento(alt.termo_busca_pncp) ||
            alt.titulo
        if (detail?.id) {
            api.registrarAnotacao(
                detail.id,
                `O gestor foi verificar atas vigentes no Ata Inteligente para ` +
                    `o conceito "${alt.titulo}" (termo de busca: ${termo}).`
            ).catch(() => {
                // best-effort — a navegação nunca é bloqueada
            })
        }
        const url = `/ata-inteligente?origem=levantamento&termo_busca=${encodeURIComponent(termo)}`
        window.open(url, '_blank', 'noopener')
    }

    // ------------------- RFI: pedido de informações ao mercado ---------------
    const abrirRfi = (nomeSolucao?: string) => {
        setRfiDestNome(nomeSolucao ?? '')
        setRfiDialogAberto(true)
    }

    const gerarRfi = async () => {
        if (!detail?.id) return
        setRfiGerando(true)
        try {
            await api.elaborarRfi(
                detail.id,
                rfiDestNome.trim() ? {nome: rfiDestNome.trim()} : undefined
            )
            setRfiDialogAberto(false)
            const d = await api.getLevantamento(detail.id)
            aplicarDetalhe(d)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setRfiGerando(false)
        }
    }

    const salvarRfi = async () => {
        if (!detail?.id || !rfiEdicao || !rfiEdicao.texto.trim()) return
        setRfiSalvando(true)
        try {
            await api.editarRfi(detail.id, rfiEdicao.id, rfiEdicao.texto.trim())
            setRfiEdicao(null)
            const d = await api.getLevantamento(detail.id)
            aplicarDetalhe(d)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setRfiSalvando(false)
        }
    }

    // Export do ofício de RFI (DOCX/PDF): trava o botão enquanto a chamada está
    // em voo — sem isso o duplo clique dispara duas exportações/downloads.
    const exportarRfiArquivo = async (rfiId: string, formato: 'docx' | 'pdf') => {
        if (!detail?.id) return
        const chave = `${rfiId}:${formato}`
        if (exportandoRfi[chave]) return
        setExportandoRfi((prev) => ({...prev, [chave]: true}))
        try {
            await api.exportarRfi(detail.id, rfiId, formato)
        } catch (e) {
            setErro(msgDeErro(e))
        } finally {
            setExportandoRfi((prev) => {
                const {[chave]: _emVoo, ...resto} = prev
                return resto
            })
        }
    }

    // ------------------------------------------------------------------ list
    const renderLista = () => (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 3,
                }}
            >
                <div>
                    <Typography
                        variant="h5"
                        sx={{fontWeight: 700, color: '#1f2b3a'}}
                    >
                        Levantamento de Mercado
                    </Typography>
                    <Typography sx={{color: '#7a8896', fontSize: 14, mt: 0.5}}>
                        Do problema à melhor solução, com método e evidências
                        (art. 18 da Lei 14.133/2021).
                    </Typography>
                </div>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={novoLevantamento}
                    sx={{
                        backgroundColor: ACCENT,
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: '10px',
                    }}
                >
                    Novo levantamento
                </Button>
            </Box>
            <Card>
                {listaLoading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                        <CircularProgress size={28} />
                    </Box>
                ) : lista.length === 0 ? (
                    <Box sx={{textAlign: 'center', py: 4}}>
                        <TravelExploreOutlinedIcon
                            sx={{fontSize: 44, color: '#c3ccd6'}}
                        />
                        <Typography sx={{color: '#7a8896', mt: 1}}>
                            Nenhum levantamento ainda. Descreva um problema e
                            deixe a Licito.guru IA mapear as alternativas do
                            mercado.
                        </Typography>
                    </Box>
                ) : (
                    <div style={{overflowX: 'auto'}}>
                        <table style={{width: '100%', borderCollapse: 'collapse'}}>
                            <thead>
                                <tr>
                                    <Th>Problema</Th>
                                    <Th>Status</Th>
                                    <Th>Criado em</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {lista.map((l) => (
                                    <tr
                                        key={l.id}
                                        style={{cursor: 'pointer'}}
                                        onClick={() => abrirLevantamento(l.id)}
                                    >
                                        <Td
                                            style={{
                                                maxWidth: 480,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {l.problema_texto}
                                        </Td>
                                        <Td>
                                            <Chip
                                                size="small"
                                                label={STATUS_LABEL[l.status]}
                                                color={statusColor(l.status)}
                                            />
                                        </Td>
                                        <Td>{dateTime(l.created_at)}</Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </>
    )

    // -------------------------------------------------------- passo 1: problema
    const renderPasso1 = () => (
        <>
            <Card>
                <SectionTitle>Qual problema você precisa resolver?</SectionTitle>
                <Typography sx={{color: '#7a8896', fontSize: 13, mb: 2}}>
                    Descreva o problema ou a necessidade que você precisa
                    resolver. Se já tem uma solução ou um fornecedor em mente,
                    cite-o expressamente (ex.: &quot;contratar a empresa
                    X&quot;) — ele aparecerá com prioridade entre as soluções
                    levantadas. A Licito.guru IA fará algumas perguntas para
                    entender o contexto.
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    placeholder="Ex.: Precisamos digitalizar um acervo de cerca de 500 mil páginas de processos físicos, com valor legal, em até 12 meses…"
                    value={problema}
                    onChange={(e) => setProblema(e.target.value)}
                    disabled={!!detail}
                />
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            md: '1fr 1fr 1fr',
                        },
                        gap: 2,
                        mt: 2,
                    }}
                >
                    {CONTEXTO_CAMPOS.map((c) => (
                        <div key={c.key}>
                            <FieldLabel>{c.label} (opcional)</FieldLabel>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={c.placeholder}
                                value={contexto[c.key] ?? ''}
                                onChange={(e) =>
                                    setContexto((prev) => ({
                                        ...prev,
                                        [c.key]: e.target.value,
                                    }))
                                }
                                disabled={!!detail}
                            />
                        </div>
                    ))}
                </Box>
                {!detail && (
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                        <Button
                            variant="contained"
                            startIcon={
                                criando ? (
                                    <CircularProgress size={16} sx={{color: '#fff'}} />
                                ) : (
                                    <AutoAwesomeIcon />
                                )
                            }
                            disabled={criando}
                            onClick={criarLevantamento}
                            sx={{
                                backgroundColor: ACCENT,
                                textTransform: 'none',
                                fontWeight: 700,
                                borderRadius: '10px',
                            }}
                        >
                            Iniciar levantamento
                        </Button>
                    </Box>
                )}
            </Card>

            {detail && (
                <Card>
                    <SectionTitle>Conversa com a Licito.guru IA</SectionTitle>
                    {detail.status === 'CONVERSANDO' && !perguntaCorrente && (
                        <Box sx={{mb: 2}}>
                            <LinearProgress />
                            <Typography sx={{color: '#7a8896', fontSize: 12, mt: 0.5}}>
                                A Licito.guru IA está analisando o problema…
                            </Typography>
                        </Box>
                    )}
                    <ChatBox ref={chatRef}>
                        {(() => {
                            // Balões otimistas ainda não confirmados pelo backend
                            // entram no fim da lista visível (dedup por papel +
                            // conteúdo contra o que o refetch já trouxe). Garante
                            // o balão final visível inclusive no estado "Conversa
                            // concluída…", pois o ChatBox é sempre renderizado.
                            const visiveis = [
                                ...detail.mensagens,
                                ...msgsOtimistas.filter(
                                    (mo) =>
                                        !detail.mensagens.some(
                                            (m) =>
                                                m.papel === mo.papel &&
                                                m.conteudo.trim() ===
                                                    mo.conteudo.trim()
                                        )
                                ),
                            ]
                            return visiveis.length === 0 ? (
                                <Typography
                                    sx={{color: '#7a8896', fontSize: 13}}
                                >
                                    Em instantes, a Licito.guru IA fará algumas
                                    perguntas adicionais.
                                </Typography>
                            ) : (
                                visiveis.map((m, i) => (
                                    <Bubble key={i} papel={m.papel}>
                                        {m.papel !== 'SISTEMA' && (
                                            <BubbleAutor>
                                                {m.papel === 'IA'
                                                    ? 'Licito.guru IA'
                                                    : 'Você'}
                                            </BubbleAutor>
                                        )}
                                        {m.conteudo}
                                    </Bubble>
                                ))
                            )
                        })()}
                    </ChatBox>
                    {perguntaCorrente ? (
                        // Perguntas de contexto UMA POR VEZ: sugestões clicáveis
                        // + campo aberto (sem obrigar digitação). Respondidas
                        // ficam compactadas no histórico acima (par no chat).
                        <Box
                            sx={{
                                mt: 2,
                                border: '1.5px solid #cfe3fa',
                                borderRadius: '12px',
                                p: 2,
                                background: '#f8fbff',
                            }}
                        >
                            <Typography
                                sx={{fontSize: 12, fontWeight: 700, color: '#7a8896'}}
                            >
                                Pergunta {numRespondidas + 1} de{' '}
                                {perguntasContexto.length}
                            </Typography>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: 15,
                                    color: '#1f2b3a',
                                    mt: 0.5,
                                }}
                            >
                                {perguntaCorrente.pergunta}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    mt: 1.5,
                                }}
                            >
                                {perguntaCorrente.sugestoes.map((s) => (
                                    <Button
                                        key={s}
                                        variant="outlined"
                                        disabled={respondendo}
                                        onClick={() =>
                                            responderPerguntaContexto(
                                                idxPerguntaCorrente,
                                                s
                                            )
                                        }
                                        sx={{
                                            justifyContent: 'flex-start',
                                            textAlign: 'left',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderRadius: '10px',
                                            borderColor: '#cfe3fa',
                                            color: '#1f2b3a',
                                        }}
                                    >
                                        {s}
                                    </Button>
                                ))}
                            </Box>
                            {!mostrarCampoCustom ? (
                                <Button
                                    size="small"
                                    disabled={respondendo}
                                    onClick={() => setMostrarCampoCustom(true)}
                                    sx={{
                                        mt: 1.5,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        color: '#7a8896',
                                    }}
                                >
                                    Responder de outra forma…
                                </Button>
                            ) : (
                                <Box sx={{display: 'flex', gap: 1, mt: 1.5}}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        autoFocus
                                        placeholder="Escreva sua resposta…"
                                        value={respostaCustom}
                                        onChange={(e) =>
                                            setRespostaCustom(e.target.value)
                                        }
                                        inputProps={{maxLength: 500}}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault()
                                                responderPerguntaContexto(
                                                    idxPerguntaCorrente,
                                                    respostaCustom
                                                )
                                            }
                                        }}
                                        disabled={respondendo}
                                    />
                                    <Button
                                        variant="contained"
                                        disabled={
                                            respondendo || !respostaCustom.trim()
                                        }
                                        onClick={() =>
                                            responderPerguntaContexto(
                                                idxPerguntaCorrente,
                                                respostaCustom
                                            )
                                        }
                                        sx={{
                                            backgroundColor: ACCENT,
                                            minWidth: 48,
                                            borderRadius: '10px',
                                        }}
                                    >
                                        {respondendo ? (
                                            <CircularProgress
                                                size={18}
                                                sx={{color: '#fff'}}
                                            />
                                        ) : (
                                            <SendIcon fontSize="small" />
                                        )}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    ) : roteiroConcluido && !conversaFechada ? (
                        // Pergunta FINAL de fecho (opcional): dá um fim claro à
                        // conversa. "Adicionar" envia o detalhe; "Concluir sem
                        // acrescentar" apenas fecha. Nos dois casos o input some
                        // e o fluxo avança como já ocorre (push do backend).
                        <Box
                            sx={{
                                mt: 2,
                                border: '1.5px solid #cfe3fa',
                                borderRadius: '12px',
                                p: 2,
                                background: '#f8fbff',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: '#7a8896',
                                }}
                            >
                                Última pergunta
                            </Typography>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: 15,
                                    color: '#1f2b3a',
                                    mt: 0.5,
                                }}
                            >
                                Há mais algum detalhe sobre a necessidade que
                                você queira acrescentar? (opcional)
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                multiline
                                minRows={2}
                                autoFocus
                                placeholder="Se quiser, acrescente aqui…"
                                value={detalheFinal}
                                onChange={(e) =>
                                    setDetalheFinal(e.target.value)
                                }
                                inputProps={{maxLength: 500}}
                                disabled={enviandoMsg}
                                sx={{mt: 1.5}}
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    mt: 1.5,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Button
                                    variant="contained"
                                    disabled={
                                        enviandoMsg || !detalheFinal.trim()
                                    }
                                    startIcon={
                                        enviandoMsg ? (
                                            <CircularProgress
                                                size={16}
                                                sx={{color: '#fff'}}
                                            />
                                        ) : (
                                            <AddIcon />
                                        )
                                    }
                                    onClick={() =>
                                        concluirConversa(detalheFinal)
                                    }
                                    sx={{
                                        backgroundColor: ACCENT,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        borderRadius: '10px',
                                    }}
                                >
                                    Adicionar
                                </Button>
                                <Button
                                    variant="outlined"
                                    disabled={enviandoMsg}
                                    onClick={() => concluirConversa()}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        borderRadius: '10px',
                                    }}
                                >
                                    Concluir sem acrescentar
                                </Button>
                            </Box>
                        </Box>
                    ) : conversaFechada ? (
                        // Conversa encerrada pelo usuário: input fechado. O
                        // avanço de etapa chega pelo push (a barra acima já
                        // indica o processamento quando ainda em CONVERSANDO).
                        <Typography
                            sx={{
                                mt: 2,
                                fontSize: 13,
                                color: '#7a8896',
                                fontStyle: 'italic',
                            }}
                        >
                            Conversa concluída. A Licito.guru IA está montando os
                            conceitos de contratação…
                        </Typography>
                    ) : (
                        <Box sx={{display: 'flex', gap: 1, mt: 2}}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Responda às perguntas ou acrescente informações…"
                                value={msgInput}
                                onChange={(e) => setMsgInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault()
                                        enviarMensagem()
                                    }
                                }}
                                disabled={
                                    enviandoMsg || detail.status === 'CONCLUIDO'
                                }
                            />
                            <Button
                                variant="contained"
                                onClick={enviarMensagem}
                                disabled={
                                    enviandoMsg ||
                                    !msgInput.trim() ||
                                    detail.status === 'CONCLUIDO'
                                }
                                sx={{
                                    backgroundColor: ACCENT,
                                    minWidth: 48,
                                    borderRadius: '10px',
                                }}
                            >
                                {enviandoMsg ? (
                                    <CircularProgress
                                        size={18}
                                        sx={{color: '#fff'}}
                                    />
                                ) : (
                                    <SendIcon fontSize="small" />
                                )}
                            </Button>
                        </Box>
                    )}
                </Card>
            )}
        </>
    )

    // Referências do MODELO de contratação (outros objetos — não entram na
    // faixa): lista secundária COLAPSADA por padrão, toggle por alternativa.
    const [modeloAberto, setModeloAberto] = useState<Record<string, boolean>>({})

    // --------------------------------------------------- passo 2: alternativas
    const renderCardAlternativa = (
        alt: AlternativaOut,
        opcoes: {
            acoes?: boolean
            investigacao?: boolean
            conceito?: boolean
            // evidenciaResumo: card "Evidências coletadas para o conceito" — não
            // lista as fontes por-documento (a citação vai só no relatório, e só
            // do que for usado). Mantém descrição, badge e "Pesquisar preços",
            // trocando as listas de fontes por um único sinal quantitativo.
            evidenciaResumo?: boolean
        }
    ) => {
        const fontesObjeto = (alt.fontes ?? []).filter(
            (f) => f.classe_referencia !== 'REFERENCIA_MODELO'
        )
        const fontesModelo = (alt.fontes ?? []).filter(
            (f) => f.classe_referencia === 'REFERENCIA_MODELO'
        )
        // Sinal quantitativo IMPESSOAL para o card-resumo: só a contagem de
        // contratações do OBJETO no PNCP (descarta documentos genéricos —
        // cartilha/checklist/guia — que não vêm do PNCP).
        const nContratacoesObjetoPncp = fontesObjeto.filter(
            (f) => (f.origem || '').toUpperCase() === 'PNCP'
        ).length
        // Conceito RECOMENDADO pela Licito.guru IA — só destaque, nunca decisão.
        // Lê a marca nova (recomendado/motivo_recomendacao, em conceito_analise
        // OU no próprio conceito) e degrada para o par legado (sugerido/porque)
        // enquanto o deploy do backend não chega — mesmo significado.
        const ca = alt.conceito_analise
        const conceitoRecomendado = !!(
            ca?.recomendado ?? alt.recomendado ?? ca?.sugerido
        )
        const motivoRecomendacao = String(
            ca?.motivo_recomendacao ??
                alt.motivo_recomendacao ??
                ca?.porque ??
                ''
        ).trim()
        return (
        <AltCard
            key={alt.id}
            escolhida={alt.id === detail?.solucao_escolhida_id}
            recomendado={!!opcoes.conceito && conceitoRecomendado}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                }}
            >
                <div>
                    <Typography sx={{fontWeight: 700, color: '#1f2b3a'}}>
                        {alt.titulo}
                    </Typography>
                    <Box sx={{display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap'}}>
                        <Chip
                            size="small"
                            label={TIPO_LABEL[alt.tipo] ?? alt.tipo}
                            sx={{backgroundColor: '#eef5ff', fontWeight: 600}}
                        />
                        {opcoes.investigacao && (
                            <Chip
                                size="small"
                                label={ALT_STATUS_LABEL[alt.status] ?? alt.status}
                                color={altStatusColor(alt.status)}
                                variant="outlined"
                            />
                        )}
                        {opcoes.conceito && conceitoRecomendado && (
                            <Chip
                                size="small"
                                icon={<AutoAwesomeIcon sx={{fontSize: 14}} />}
                                label="Recomendado pela Licito.guru IA"
                                color="success"
                                sx={{fontWeight: 600}}
                            />
                        )}
                        {opcoes.investigacao && renderVinculoIn65(alt)}
                        {opcoes.investigacao && alt.possivel_inexigibilidade && (
                            <Chip
                                size="small"
                                icon={<GavelOutlinedIcon sx={{fontSize: 14}} />}
                                label="Possível inexigibilidade (art. 74)"
                                color="warning"
                                variant="outlined"
                                sx={{fontWeight: 600}}
                            />
                        )}
                    </Box>
                </div>
                {opcoes.investigacao && (
                    <Button
                        size="small"
                        startIcon={<RequestQuoteOutlinedIcon fontSize="small" />}
                        onClick={() => pesquisarPrecosDaAlternativa(alt)}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            color: '#7a8896',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Pesquisar preços (IN 65)
                    </Button>
                )}
                {opcoes.acoes && (
                    <Box sx={{display: 'flex', gap: 0.5}}>
                        <Tooltip title="Editar alternativa">
                            <IconButton
                                size="small"
                                onClick={() => abrirDialogAlternativa(alt)}
                            >
                                <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Remover alternativa">
                            <IconButton
                                size="small"
                                onClick={() => removerAlternativa(alt)}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
            <Typography sx={{color: '#4a5563', fontSize: 14, mt: 1}}>
                {alt.descricao}
            </Typography>

            {opcoes.conceito && alt.conceito_analise && (
                <Box
                    sx={{
                        mt: 1.5,
                        p: 1.5,
                        borderRadius: '10px',
                        background: '#f6f8fa',
                        border: '1px solid #eef1f4',
                    }}
                >
                    {alt.conceito_analise.resumo && (
                        <Typography sx={{fontSize: 13, fontWeight: 600, mb: 0.5}}>
                            {alt.conceito_analise.resumo}
                        </Typography>
                    )}
                    {(alt.conceito_analise.pros?.length ?? 0) > 0 && (
                        <Typography sx={{fontSize: 13, color: '#14532d'}}>
                            + {alt.conceito_analise.pros?.join(' · ')}
                        </Typography>
                    )}
                    {(alt.conceito_analise.contras?.length ?? 0) > 0 && (
                        <Typography sx={{fontSize: 13, color: '#7c2d12'}}>
                            − {alt.conceito_analise.contras?.join(' · ')}
                        </Typography>
                    )}
                    {conceitoRecomendado && motivoRecomendacao && (
                        <Box
                            sx={{
                                mt: 0.75,
                                p: 1,
                                borderRadius: '8px',
                                background: '#eaf7ee',
                                border: '1px solid #cdead4',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: '#14532d',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                <AutoAwesomeIcon sx={{fontSize: 13}} />
                                Recomendado pela Licito.guru IA — sugestão, a
                                decisão é sua
                            </Typography>
                            <Typography
                                sx={{fontSize: 13, color: '#1f2b3a', mt: 0.25}}
                            >
                                {motivoRecomendacao}
                            </Typography>
                        </Box>
                    )}
                    {alt.conceito_analise.sugerido &&
                        alt.conceito_analise.evidencia && (
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: '#14532d',
                                    mt: 0.5,
                                }}
                            >
                                {alt.conceito_analise.evidencia}
                            </Typography>
                        )}
                    {(() => {
                        // PANORAMA do mercado (números determinísticos do
                        // PNCP + players verificados): como se contrata,
                        // quanto e quem — linguagem de indício.
                        const p = alt.conceito_analise?.panorama
                        if (!p) return null
                        const partes: string[] = []
                        if (p.n_recentes != null) {
                            partes.push(
                                `${p.n_recentes} contratação(ões) recente(s)`
                            )
                        }
                        const s = p.sinal
                        if (
                            s?.sinal === 'POSSIVEL_INEXIGIBILIDADE' &&
                            s.n_total > 0
                        ) {
                            partes.push(
                                `majoritariamente inexigibilidade ` +
                                    `(${s.n_inexigibilidade} de ${s.n_total})`
                            )
                        } else if (
                            s?.sinal === 'CAMINHO_LICITACAO' &&
                            s.n_total > 0
                        ) {
                            partes.push(
                                `majoritariamente modalidades competitivas ` +
                                    `(${s.n_competitivas} de ${s.n_total})`
                            )
                        }
                        if (p.players?.length) {
                            partes.push(`players: ${p.players.join(', ')}`)
                        }
                        if (partes.length === 0) return null
                        return (
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: '#3730a3',
                                    mt: 0.5,
                                }}
                            >
                                No PNCP: {partes.join(' · ')}
                            </Typography>
                        )
                    })()}
                </Box>
            )}
            {opcoes.conceito && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1,
                        mt: 1.5,
                        flexWrap: 'wrap',
                    }}
                >
                    {alt.tipo === 'ATA_CARONA' && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                                <TravelExploreOutlinedIcon fontSize="small" />
                            }
                            onClick={() => verificarAtas(alt)}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                borderRadius: '10px',
                            }}
                        >
                            Verificar atas vigentes no Ata Inteligente
                        </Button>
                    )}
                    <Button
                        variant={conceitoRecomendado ? 'contained' : 'outlined'}
                        size="small"
                        disabled={!!escolhendoConceitoId}
                        startIcon={
                            escolhendoConceitoId === alt.id ? (
                                <CircularProgress size={14} />
                            ) : undefined
                        }
                        onClick={() => escolherConceito(alt)}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '10px',
                            ...(conceitoRecomendado
                                ? {backgroundColor: ACCENT}
                                : {}),
                        }}
                    >
                        Escolher este conceito
                    </Button>
                </Box>
            )}

            {opcoes.investigacao && !opcoes.evidenciaResumo && alt.faixa_preco && (
                <FaixaGrid>
                    <FaixaItem>
                        <Typography sx={{fontSize: 11, color: '#7a8896'}}>
                            Mínimo
                        </Typography>
                        <Typography sx={{fontWeight: 700, fontSize: 14}}>
                            {currency(alt.faixa_preco.min)}
                        </Typography>
                    </FaixaItem>
                    <FaixaItem>
                        <Typography sx={{fontSize: 11, color: '#7a8896'}}>
                            Mediana
                        </Typography>
                        <Typography sx={{fontWeight: 700, fontSize: 14}}>
                            {currency(alt.faixa_preco.mediana)}
                        </Typography>
                    </FaixaItem>
                    <FaixaItem>
                        <Typography sx={{fontSize: 11, color: '#7a8896'}}>
                            Máximo
                        </Typography>
                        <Typography sx={{fontWeight: 700, fontSize: 14}}>
                            {currency(alt.faixa_preco.max)}
                        </Typography>
                    </FaixaItem>
                    <FaixaItem>
                        <Typography sx={{fontSize: 11, color: '#7a8896'}}>
                            Fontes
                        </Typography>
                        <Typography sx={{fontWeight: 700, fontSize: 14}}>
                            {alt.faixa_preco.n_fontes}
                        </Typography>
                    </FaixaItem>
                </FaixaGrid>
            )}
            {opcoes.investigacao &&
                (!alt.faixa_preco || (alt.faixa_preco.n_fontes ?? 0) < 2) &&
                alt.status === 'INVESTIGADA' && (
                    <Typography sx={{color: '#7a8896', fontSize: 13, mt: 1.5}}>
                        Mercado com poucas referências públicas de preço
                        pertinentes ao objeto para esta alternativa — isso é um
                        achado do levantamento e entra na análise de restrição
                        de mercado.
                    </Typography>
                )}
            {opcoes.investigacao &&
                in65Info(alt)?.status === 'CONCLUIDO' &&
                in65Info(alt)?.valor_total_estimado != null && (
                    <Typography
                        sx={{mt: 1, fontSize: 13, fontWeight: 700, color: '#14532d'}}
                    >
                        Valor de referência (pesquisa IN 65 vinculada):{' '}
                        {currency(in65Info(alt)?.valor_total_estimado)}
                    </Typography>
                )}

            {opcoes.investigacao &&
                (alt.solucoes?.itens?.length ?? 0) > 0 && (
                    <Box sx={{mt: 2}}>
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#7a8896',
                                mb: 0.5,
                            }}
                        >
                            Soluções identificadas no mercado
                        </Typography>
                        {(alt.solucoes?.itens ?? [])
                            .filter((s) => s.contratavel !== false)
                            .map((s, i) => (
                            <SolucaoRow key={i}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 14,
                                            color: '#1f2b3a',
                                        }}
                                    >
                                        {s.nome_solucao}
                                    </Typography>
                                    {s.fornecedor &&
                                        s.fornecedor !== s.nome_solucao && (
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    color: '#7a8896',
                                                }}
                                            >
                                                · {s.fornecedor}
                                            </Typography>
                                        )}
                                    {s.site && (
                                        <IconButton
                                            size="small"
                                            component="a"
                                            href={s.site}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={`Abrir o site de ${s.nome_solucao} em nova aba`}
                                        >
                                            <OpenInNewIcon
                                                sx={{fontSize: 16}}
                                            />
                                        </IconButton>
                                    )}
                                </Box>
                                {s.descricao_1_linha && (
                                    <Typography
                                        sx={{fontSize: 13, color: '#4a5563'}}
                                    >
                                        {s.descricao_1_linha}
                                    </Typography>
                                )}
                                <EvidenciaPncp s={s} />
                            </SolucaoRow>
                        ))}
                        {(alt.solucoes?.itens ?? []).some(
                            (s) => s.contratavel === false
                        ) && (
                            <Box sx={{mt: 1}}>
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: '#7a8896',
                                        mb: 0.5,
                                    }}
                                >
                                    Referências de tecnologia pública (não
                                    contratáveis)
                                </Typography>
                                {(alt.solucoes?.itens ?? [])
                                    .filter((s) => s.contratavel === false)
                                    .map((s, i) => (
                                        <SolucaoRow key={`pub-${i}`}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: 14,
                                                        color: '#1f2b3a',
                                                    }}
                                                >
                                                    {s.nome_solucao}
                                                </Typography>
                                                {s.fornecedor &&
                                                    s.fornecedor !==
                                                        s.nome_solucao && (
                                                        <Typography
                                                            sx={{
                                                                fontSize: 13,
                                                                color: '#7a8896',
                                                            }}
                                                        >
                                                            · {s.fornecedor}
                                                        </Typography>
                                                    )}
                                                <Chip
                                                    size="small"
                                                    label="ferramenta interna de órgão público"
                                                    variant="outlined"
                                                />
                                            </Box>
                                            {s.descricao_1_linha && (
                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        color: '#4a5563',
                                                    }}
                                                >
                                                    {s.descricao_1_linha}
                                                </Typography>
                                            )}
                                        </SolucaoRow>
                                    ))}
                            </Box>
                        )}
                    </Box>
                )}
            {opcoes.investigacao &&
                alt.status === 'INVESTIGADA' &&
                (alt.solucoes?.itens?.length ?? 0) === 0 &&
                alt.solucoes?.busca_status === 'FALHOU' && (
                    <Typography
                        sx={{fontSize: 13, color: '#7c2d12', mt: 1.5}}
                    >
                        Não foi possível pesquisar soluções de mercado agora
                        (fonte de busca indisponível) — isso NÃO significa que
                        não existam.{' '}
                        <Button
                            size="small"
                            disabled={repesquisando}
                            onClick={repesquisarSolucoes}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                p: 0,
                                minWidth: 0,
                            }}
                        >
                            {repesquisando
                                ? 'Repesquisando…'
                                : 'Tentar novamente'}
                        </Button>
                    </Typography>
                )}

            {opcoes.investigacao && alt.possivel_inexigibilidade && (
                <Alert
                    severity="warning"
                    icon={<GavelOutlinedIcon fontSize="small" />}
                    sx={{mt: 1.5, borderRadius: '10px'}}
                >
                    <Typography sx={{fontSize: 13, fontWeight: 700, mb: 0.5}}>
                        Há indícios de possível inexigibilidade (art. 74 da Lei
                        14.133/2021)
                    </Typography>
                    {alt.solucoes?.sinal_conceito?.sinal ===
                        'POSSIVEL_INEXIGIBILIDADE' && (
                        <Typography
                            sx={{fontSize: 13, fontWeight: 700, mb: 0.5}}
                        >
                            {fraseSinal(alt.solucoes.sinal_conceito)}
                        </Typography>
                    )}
                    <Typography sx={{fontSize: 13}}>
                        As evidências coletadas sugerem — mas NÃO comprovam —
                        inviabilidade de competição para esta alternativa. Para
                        contratar por inexigibilidade, o processo precisa
                        demonstrar, pelos meios do art. 74: a exclusividade do
                        fornecedor com documentação idônea (não basta declaração
                        do próprio interessado), a justificativa do preço e as
                        razões da escolha. A confirmação é do gestor.
                    </Typography>
                    <Button
                        size="small"
                        onClick={() => navigate('/contratacao-direta')}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            mt: 0.5,
                            p: 0,
                        }}
                    >
                        Montar justificativa de inexigibilidade →
                    </Button>
                </Alert>
            )}
            {opcoes.investigacao &&
                !alt.possivel_inexigibilidade &&
                alt.solucoes?.sinal_conceito?.sinal === 'CAMINHO_LICITACAO' && (
                    // Sentido OPOSTO, dito com todas as letras (decisão do
                    // dono): sem indício de inexigibilidade → licitação.
                    <Alert severity="info" sx={{mt: 1.5, borderRadius: '10px'}}>
                        <Typography
                            sx={{fontSize: 13, fontWeight: 700, mb: 0.5}}
                        >
                            Sem indício de inexigibilidade — caminho provável:
                            licitação
                        </Typography>
                        <Typography sx={{fontSize: 13}}>
                            {fraseSinal(alt.solucoes.sinal_conceito)} Você pode
                            concluir a escolha sem preferência por solução
                            específica, com requisitos técnicos mínimos
                            derivados da comparação. A definição cabe ao gestor.
                        </Typography>
                    </Alert>
                )}

            {opcoes.investigacao &&
                !opcoes.evidenciaResumo &&
                fontesObjeto.length > 0 && (
                <Box sx={{mt: 2}}>
                    <Typography
                        sx={{fontSize: 12, fontWeight: 700, color: '#7a8896', mb: 0.5}}
                    >
                        Contratações e referências encontradas
                    </Typography>
                    <div style={{overflowX: 'auto'}}>
                        <table style={{width: '100%', borderCollapse: 'collapse'}}>
                            <thead>
                                <tr>
                                    <Th>Fonte</Th>
                                    <Th>Órgão</Th>
                                    <Th>Valor</Th>
                                    <Th>Link</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {fontesObjeto.map((f, i) => (
                                    <tr key={i}>
                                        <Td
                                            style={{
                                                maxWidth: 320,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {f.titulo}
                                        </Td>
                                        <Td>{f.orgao || '—'}</Td>
                                        <Td>{currency(f.valor)}</Td>
                                        <Td>
                                            {f.url ? (
                                                <IconButton
                                                    size="small"
                                                    component="a"
                                                    href={f.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label="Abrir fonte em nova aba"
                                                >
                                                    <OpenInNewIcon fontSize="small" />
                                                </IconButton>
                                            ) : (
                                                '—'
                                            )}
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Box>
            )}

            {opcoes.investigacao &&
                !opcoes.evidenciaResumo &&
                fontesModelo.length > 0 && (
                <Box sx={{mt: 1.5}}>
                    <Button
                        size="small"
                        onClick={() =>
                            setModeloAberto((prev) => ({
                                ...prev,
                                [alt.id]: !prev[alt.id],
                            }))
                        }
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            color: '#7a8896',
                            p: 0,
                            minWidth: 0,
                            textAlign: 'left',
                        }}
                    >
                        {modeloAberto[alt.id] ? '▾' : '▸'} Referências do modelo
                        de contratação ({fontesModelo.length}) — outros objetos,
                        não entram na faixa
                    </Button>
                    {modeloAberto[alt.id] && (
                        <Box component="ul" sx={{m: '6px 0 0', pl: 2.5}}>
                            {fontesModelo.slice(0, 8).map((f, i) => (
                                <Box
                                    component="li"
                                    key={i}
                                    sx={{
                                        fontSize: 13,
                                        color: '#7a8896',
                                        mb: 0.5,
                                    }}
                                >
                                    {f.titulo}
                                    {f.orgao ? ` (${f.orgao})` : ''}
                                    {f.valor != null
                                        ? ` — ${currency(f.valor)}`
                                        : ''}
                                    {f.url ? (
                                        <a
                                            href={f.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{marginLeft: 6}}
                                        >
                                            [link]
                                        </a>
                                    ) : null}
                                </Box>
                            ))}
                            {fontesModelo.length > 8 && (
                                <Box
                                    component="li"
                                    sx={{fontSize: 13, color: '#7a8896'}}
                                >
                                    … e mais {fontesModelo.length - 8}{' '}
                                    referência(s).
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            )}
            {opcoes.evidenciaResumo && nContratacoesObjetoPncp > 0 && (
                <Typography sx={{fontSize: 13, color: '#4a5563', mt: 1.5}}>
                    {nContratacoesObjetoPncp} contratação(ões) do objeto
                    encontrada(s) no PNCP. As referências utilizadas são citadas
                    no relatório.
                </Typography>
            )}
        </AltCard>
        )
    }

    const renderPasso2 = () => (
        <Card>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                }}
            >
                <SectionTitle style={{margin: 0}}>
                    Mapa de alternativas
                </SectionTitle>
                <Button
                    startIcon={<AddIcon />}
                    onClick={() => abrirDialogAlternativa()}
                    sx={{textTransform: 'none', fontWeight: 700}}
                >
                    Adicionar alternativa
                </Button>
            </Box>
            <Typography sx={{color: '#7a8896', fontSize: 13, mb: 2}}>
                A Licito.guru IA propôs os tipos de solução abaixo a partir da
                conversa. Ajuste o mapa: remova o que não se aplica e adicione
                alternativas que conhece — o TCU pede um conjunto representativo
                de soluções antes de especificar.
            </Typography>
            {alternativas.length === 0 ? (
                <Box sx={{textAlign: 'center', py: 3}}>
                    <CircularProgress size={22} />
                    <Typography sx={{color: '#7a8896', fontSize: 13, mt: 1}}>
                        Aguardando o mapa de alternativas da Licito.guru IA…
                    </Typography>
                </Box>
            ) : (
                alternativas.map((alt) =>
                    renderCardAlternativa(alt, {acoes: true})
                )
            )}
            <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 1}}>
                <Button
                    variant="contained"
                    startIcon={
                        investigandoReq ? (
                            <CircularProgress size={16} sx={{color: '#fff'}} />
                        ) : (
                            <TravelExploreOutlinedIcon />
                        )
                    }
                    disabled={
                        investigandoReq ||
                        alternativasAtivas.length === 0 ||
                        detail?.status === 'INVESTIGANDO'
                    }
                    onClick={investigar}
                    sx={{
                        backgroundColor: ACCENT,
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: '10px',
                    }}
                >
                    Investigar mercado das alternativas
                </Button>
            </Box>
        </Card>
    )

    // -------------------------------------------------- passo 3: investigação
    const renderPasso3 = () => (
        <Card>
            <SectionTitle>Investigação de mercado</SectionTitle>
            {detail?.status === 'INVESTIGANDO' && (
                <Box sx={{mb: 2}}>
                    <LinearProgress />
                    <Typography sx={{color: '#7a8896', fontSize: 12, mt: 0.5}}>
                        Buscando contratações similares no PNCP e referências de
                        mercado para cada alternativa…
                    </Typography>
                </Box>
            )}
            {investigacaoConcluida && (
                <Alert severity="success" sx={{mb: 2}}>
                    Investigação concluída. Confira as faixas de preço e as
                    contratações similares de cada alternativa.
                </Alert>
            )}
            {alternativas.map((alt) =>
                renderCardAlternativa(alt, {investigacao: true})
            )}
        </Card>
    )

    // Bloco da justificativa (compartilhado: passo de escolha dos dois fluxos).
    const renderBlocoJustificativa = () =>
        justificativa || detail?.justificativa_final ? (
            <Box sx={{mt: 3}}>
                <FieldLabel>
                    Justificativa técnica e econômica (edite à vontade)
                </FieldLabel>
                {/* Transparência na UI, NUNCA no texto: o conteúdo do campo
                    é 100% copiável para o processo administrativo. */}
                <Typography sx={{fontSize: 12, color: '#7a8896', mb: 1}}>
                    Os motivos abaixo foram sugeridos pela Licito.guru IA a
                    partir dos dados do levantamento. Registre suas observações
                    no passo de escolha para que sejam incorporadas como razões
                    do gestor.
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    minRows={8}
                    value={justificativa}
                    onChange={(e) => setJustificativa(e.target.value)}
                />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 1.5,
                    }}
                >
                    <Button
                        variant="outlined"
                        disabled={salvandoJust || !justificativa.trim()}
                        onClick={salvarJustificativa}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '10px',
                        }}
                    >
                        {salvandoJust ? 'Salvando…' : 'Salvar justificativa'}
                    </Button>
                </Box>
            </Box>
        ) : null

    // ------------------- fluxo novo: passo 2 (conceitos) ---------------------
    const renderPassoConceitos = () => (
        <Card>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                }}
            >
                <SectionTitle style={{margin: 0}}>
                    Conceitos de contratação
                </SectionTitle>
                <Button
                    startIcon={<AddIcon />}
                    onClick={() => abrirDialogAlternativa()}
                    sx={{textTransform: 'none', fontWeight: 700}}
                >
                    Adicionar conceito
                </Button>
            </Box>
            <Typography sx={{color: '#7a8896', fontSize: 13, mb: 2}}>
                A Licito.guru IA mapeou e comparou os conceitos (modelos de
                contratação) aplicáveis ao seu problema, ancorada na prática de
                mercado observada — a escolha é sua. Ao escolher, a
                investigação de mercado roda apenas para o conceito escolhido.
            </Typography>
            {alternativasAtivas.some((a) => a.conceito_analise) &&
                !alternativasAtivas.some(
                    (a) => a.conceito_analise?.sugerido
                ) && (
                    <Alert severity="info" sx={{mb: 2, borderRadius: '10px'}}>
                        Dados insuficientes para sugerir — compare os conceitos
                        abaixo.
                        {(() => {
                            const porque = alternativasAtivas
                                .map(
                                    (a) =>
                                        a.conceito_analise?.sem_sugestao_porque
                                )
                                .find(Boolean)
                            return porque ? ` ${porque}` : ''
                        })()}
                    </Alert>
                )}
            {alternativasAtivas.length === 0 ? (
                <Box sx={{textAlign: 'center', py: 3}}>
                    <CircularProgress size={22} />
                    <Typography sx={{color: '#7a8896', fontSize: 13, mt: 1}}>
                        Aguardando o mapa de conceitos da Licito.guru IA…
                    </Typography>
                </Box>
            ) : (
                alternativasAtivas.map((alt) =>
                    renderCardAlternativa(alt, {acoes: true, conceito: true})
                )
            )}
        </Card>
    )

    // ---------- passo 3: qual é o caminho da contratação? (art. 74) ----------
    // O sistema PROPÕE com o trecho literal à vista; o gestor DECLARA. Silêncio,
    // dúvida ou falha de coleta caem sempre no caminho competitivo — errar para
    // "competitivo" custa um passo ao gestor; errar para "direta" custa o patrimônio
    // dele (art. 73: responsabilidade solidária por dano ao erário quando há dolo,
    // fraude ou erro grosseiro).
    const hipoteseSugerida = detail?.hipotese_sugerida || null
    const hipoteseDeclarada = detail?.hipotese_contratacao || null
    const [declarandoHipotese, setDeclarandoHipotese] = useState(false)

    const declararHipotese = async (hipotese: Hipotese74 | null, alinea?: string | null) => {
        if (!detail) return
        setDeclarandoHipotese(true)
        try {
            const atualizado = await api.declararHipotese(detail.id, hipotese, alinea)
            setDetail(atualizado)
        } catch (e) {
            console.error('Falha ao declarar a hipótese de contratação:', e)
        } finally {
            setDeclarandoHipotese(false)
        }
    }

    const CartaoCaminho = ({
        titulo,
        descricao,
        destacado,
        ativo,
        onClick,
    }: {
        titulo: string
        descricao: string
        destacado?: boolean
        ativo?: boolean
        onClick: () => void
    }) => (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            disabled={declarandoHipotese}
            sx={{
                textAlign: 'left',
                cursor: 'pointer',
                border: '1px solid',
                borderColor: ativo ? '#1f6feb' : destacado ? '#f0b429' : '#e3e8ee',
                bgcolor: ativo ? '#eff6ff' : destacado ? '#fffbeb' : '#fff',
                borderRadius: '10px',
                p: 1.5,
                flex: '1 1 260px',
                '&:hover': {borderColor: '#1f6feb'},
            }}
        >
            <Typography sx={{fontSize: 13, fontWeight: 700, color: '#1f2b3a'}}>
                {titulo}
            </Typography>
            <Typography sx={{fontSize: 12.5, color: '#5a6b7d', mt: 0.5}}>
                {descricao}
            </Typography>
        </Box>
    )

    const renderEscolhaDoCaminho = () => {
        const sugereDireta = !!hipoteseSugerida
        const declarada = hipoteseDeclarada?.hipotese ?? null
        const jaDeclarou = !!hipoteseDeclarada
        return (
            <Box sx={{mb: 2.5}}>
                <Typography sx={{fontSize: 13, fontWeight: 700, color: '#1f2b3a', mb: 0.5}}>
                    Qual é o caminho desta contratação?
                </Typography>
                {sugereDireta && hipoteseSugerida?.trecho && (
                    <Alert severity="info" sx={{mb: 1.5, borderRadius: '10px'}}>
                        <Typography sx={{fontSize: 13}}>
                            Você escreveu{' '}
                            <em>“{hipoteseSugerida.trecho}”</em> — pelo que descreveu,
                            {hipoteseSugerida.hipotese === 'ART74_I'
                                ? ' pode ser que só exista um fornecedor capaz.'
                                : ' quem executa parece ser o próprio objeto.'}{' '}
                            Confirme abaixo. A decisão é sua.
                        </Typography>
                    </Alert>
                )}
                <Box sx={{display: 'flex', gap: 1.5, flexWrap: 'wrap'}}>
                    <CartaoCaminho
                        titulo="Comparar o que o mercado oferece"
                        descricao="Caminho competitivo: as soluções encontradas entram na comparação."
                        ativo={jaDeclarou && declarada === null}
                        onClick={() => declararHipotese(null)}
                    />
                    <CartaoCaminho
                        titulo="Acredito que só exista um fornecedor capaz"
                        descricao="A comparação continua — é ela que comprova (ou refuta) a exclusividade."
                        destacado={hipoteseSugerida?.hipotese === 'ART74_I'}
                        ativo={declarada === 'ART74_I'}
                        onClick={() => declararHipotese('ART74_I')}
                    />
                    <CartaoCaminho
                        titulo="Preciso do trabalho desta pessoa ou escritório"
                        descricao="Quem executa é o objeto: em vez de comparar, o levantamento investiga o profissional."
                        destacado={
                            hipoteseSugerida?.hipotese === 'ART74_III' ||
                            hipoteseSugerida?.hipotese === 'ART74_II'
                        }
                        ativo={declarada === 'ART74_III' || declarada === 'ART74_II'}
                        onClick={() =>
                            declararHipotese(
                                hipoteseSugerida?.hipotese === 'ART74_II'
                                    ? 'ART74_II'
                                    : 'ART74_III',
                                hipoteseSugerida?.alinea ?? null
                            )
                        }
                    />
                </Box>
                {jaDeclarou && declarada !== null && (
                    <Alert severity="warning" sx={{mt: 1.5, borderRadius: '10px'}}>
                        <Typography sx={{fontSize: 12.5}}>
                            <strong>{hipoteseDeclarada?.rotulo}</strong> — contratação
                            direta indevida sujeita o agente à responsabilidade solidária
                            pelo dano ao erário quando houver dolo, fraude ou erro
                            grosseiro (art. 73). O levantamento reúne os elementos; a
                            decisão e a assinatura são suas.
                            {hipoteseDeclarada?.divergiu && (
                                <> A sua declaração divergiu da sugestão do sistema, e
                                isso constará do relatório.</>
                            )}
                        </Typography>
                    </Alert>
                )}
            </Box>
        )
    }

    // ---------- dossiê da notória especialização (art. 74, §3º) ----------
    const [montandoDossie, setMontandoDossie] = useState(false)
    const dossie = hipoteseDeclarada?.dossie || null

    const montarDossie = async () => {
        if (!detail) return
        setMontandoDossie(true)
        try {
            setDetail(await api.montarDossie(detail.id))
        } catch (e) {
            console.error('Falha ao montar o dossiê:', e)
        } finally {
            setMontandoDossie(false)
        }
    }

    const CORES_SELO: Record<string, {bg: string; fg: string; rotulo: string}> = {
        VERIFICADO: {bg: '#ecfdf5', fg: '#065f46', rotulo: 'verificado'},
        DECLARADO_PELO_GESTOR: {bg: '#eff6ff', fg: '#1e40af', rotulo: 'declarado por você'},
        NAO_LOCALIZADO: {bg: '#fff7ed', fg: '#9a3412', rotulo: 'não localizado'},
    }

    const renderDossie = () => {
        if (!hipoteseDeclarada || hipoteseDeclarada.mantem_comparacao) return null
        return (
            <Box sx={{mt: 2.5, pt: 2, borderTop: '1px solid #e3e8ee'}}>
                <Typography sx={{fontSize: 13, fontWeight: 700, color: '#1f2b3a'}}>
                    Notória especialização (art. 74, §3º)
                </Typography>
                <Typography sx={{fontSize: 12.5, color: '#5a6b7d', mt: 0.5, mb: 1.5}}>
                    Reunimos, em fontes públicas, os elementos que a lei enumera:
                    desempenho anterior, estudos e publicações, experiência, organização e
                    equipe técnica.
                </Typography>

                {!dossie && (
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={montandoDossie}
                        onClick={montarDossie}
                        sx={{textTransform: 'none'}}
                    >
                        {montandoDossie
                            ? 'Investigando o profissional…'
                            : 'Investigar o profissional'}
                    </Button>
                )}

                {dossie && (
                    <>
                        <Typography sx={{fontSize: 13, fontWeight: 700, mb: 1}}>
                            {dossie.pessoa}
                            {dossie.cnpj ? ` — CNPJ ${dossie.cnpj}` : ''}
                        </Typography>

                        {Object.entries(dossie.por_elemento).map(([chave, itens]) =>
                            itens.length === 0 ? null : (
                                <Box key={chave} sx={{mb: 1.5}}>
                                    {itens.map((it, idx) => {
                                        const c = CORES_SELO[it.selo] || CORES_SELO.NAO_LOCALIZADO
                                        return (
                                            <Box
                                                key={`${chave}-${idx}`}
                                                sx={{
                                                    p: 1.2,
                                                    mb: 0.8,
                                                    borderRadius: '8px',
                                                    bgcolor: c.bg,
                                                    border: '1px solid #e3e8ee',
                                                }}
                                            >
                                                <Box sx={{display: 'flex', gap: 1, alignItems: 'baseline'}}>
                                                    <Chip
                                                        size="small"
                                                        label={c.rotulo}
                                                        sx={{fontSize: '.66rem', color: c.fg, bgcolor: '#fff'}}
                                                    />
                                                    <Typography sx={{fontSize: 12, color: '#5a6b7d'}}>
                                                        {chave.replace(/_/g, ' ')}
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{fontSize: 12.5, mt: 0.5}}>
                                                    {it.descricao}
                                                </Typography>
                                                {it.base_url && (
                                                    <a
                                                        href={it.base_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{fontSize: 12, color: '#1f6feb'}}
                                                    >
                                                        ver fonte
                                                    </a>
                                                )}
                                            </Box>
                                        )
                                    })}
                                </Box>
                            )
                        )}

                        {dossie.lacunas.length > 0 && (
                            <Alert severity="warning" sx={{borderRadius: '10px', mb: 1}}>
                                <Typography sx={{fontSize: 12.5}}>
                                    <strong>Ainda sem comprovação:</strong>{' '}
                                    {dossie.lacunas.join(', ')}. Junte os documentos ao
                                    processo — o profissional pode fornecê-los.
                                </Typography>
                            </Alert>
                        )}
                        <Typography sx={{fontSize: 11.5, color: '#7a8896'}}>
                            {dossie.ressalva}
                        </Typography>
                    </>
                )}
            </Box>
        )
    }

    // ------------------- fluxo novo: passo 3 (soluções) ----------------------
    const renderPassoSolucoes = () => {
        const investigando = detail?.status === 'INVESTIGANDO'
        const buscaFalhou =
            conceitoEscolhido?.solucoes?.busca_status === 'FALHOU'
        return (
            <Card>
                <SectionTitle>Soluções do conceito escolhido</SectionTitle>
                {conceitoEscolhido && (
                    <Typography sx={{color: '#7a8896', fontSize: 13, mb: 2}}>
                        Conceito: <strong>{conceitoEscolhido.titulo}</strong>
                        {hipoteseDeclarada && !hipoteseDeclarada.mantem_comparacao ? (
                            <> — quem executa é o objeto desta contratação, então não há
                            concorrentes a comparar: o levantamento passa a demonstrar
                            por que esta escolha é a adequada.</>
                        ) : (
                            <> — marque as soluções que entram na comparação (mínimo 1)
                            e adicione outras que você conhece.</>
                        )}
                    </Typography>
                )}
                {renderEscolhaDoCaminho()}
                {renderDossie()}
                {investigando && (
                    <Box sx={{mb: 2}}>
                        <LinearProgress />
                        <Typography sx={{color: '#7a8896', fontSize: 12, mt: 0.5}}>
                            Investigando soluções nomeadas, contratações
                            similares e referências de mercado do conceito…
                        </Typography>
                    </Box>
                )}
                {!investigando && buscaFalhou && (
                    <Alert severity="warning" sx={{mb: 2, borderRadius: '10px'}}>
                        <Typography sx={{fontSize: 13, fontWeight: 700}}>
                            Não foi possível pesquisar soluções agora
                        </Typography>
                        <Typography sx={{fontSize: 13}}>
                            A fonte de busca externa está indisponível — isso
                            NÃO significa que não existam soluções no mercado.
                            Você pode adicionar soluções manualmente ou tentar
                            de novo.
                        </Typography>
                        <Button
                            size="small"
                            startIcon={
                                repesquisando ? (
                                    <CircularProgress size={14} />
                                ) : (
                                    <RefreshIcon fontSize="small" />
                                )
                            }
                            disabled={repesquisando}
                            onClick={repesquisarSolucoes}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                mt: 0.5,
                                p: 0,
                            }}
                        >
                            {repesquisando
                                ? 'Repesquisando…'
                                : 'Tentar novamente'}
                        </Button>
                    </Alert>
                )}
                {!investigando && !buscaFalhou && itensSolucoes.length === 0 && (
                    <Typography sx={{color: '#7a8896', fontSize: 13, mb: 2}}>
                        A Licito.guru IA ainda está identificando soluções no
                        mercado — elas aparecem aqui em instantes. Você também
                        pode adicionar soluções manualmente abaixo.
                    </Typography>
                )}
                {itensSolucoes.map((s) => (
                    <SolucaoRow key={s.nome_solucao}>
                        <FormControlLabel
                            sx={{alignItems: 'flex-start', m: 0}}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={marcada(
                                        s.nome_solucao,
                                        s.selecionada
                                    )}
                                    onChange={(e) =>
                                        setSelecaoNomes((prev) => ({
                                            ...prev,
                                            [s.nome_solucao]: e.target.checked,
                                        }))
                                    }
                                    sx={{mt: -0.5}}
                                />
                            }
                            label={
                                <Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <Typography
                                            sx={{fontWeight: 700, fontSize: 14}}
                                        >
                                            {s.nome_solucao}
                                        </Typography>
                                        {s.fornecedor &&
                                            s.fornecedor !== s.nome_solucao && (
                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        color: '#7a8896',
                                                    }}
                                                >
                                                    · {s.fornecedor}
                                                </Typography>
                                            )}
                                        {s.site && (
                                            <IconButton
                                                size="small"
                                                component="a"
                                                href={s.site}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={`Abrir o site de ${s.nome_solucao} em nova aba`}
                                            >
                                                <OpenInNewIcon
                                                    sx={{fontSize: 16}}
                                                />
                                            </IconButton>
                                        )}
                                        {s.origem ===
                                            'solicitada_usuario' && (
                                            <Chip
                                                size="small"
                                                label="solicitada por você"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        )}
                                        {s.origem === 'manual' && (
                                            <Chip
                                                size="small"
                                                label="adicionada por você"
                                                variant="outlined"
                                            />
                                        )}
                                        {s.indicio_exclusividade && (
                                            <Chip
                                                size="small"
                                                label="indício de exclusividade"
                                                color="warning"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                    {s.descricao_1_linha && (
                                        <Typography
                                            sx={{
                                                fontSize: 13,
                                                color: '#4a5563',
                                            }}
                                        >
                                            {s.descricao_1_linha}
                                        </Typography>
                                    )}
                                </Box>
                            }
                        />
                        {/* Evidência clicável FORA da label do checkbox (não
                            interfere no clique de seleção; a11y). */}
                        <EvidenciaPncp s={s} />
                    </SolucaoRow>
                ))}
                {itensNaoContrataveis.length > 0 && (
                    <Box sx={{mt: 1, mb: 1}}>
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#7a8896',
                                mb: 0.5,
                            }}
                        >
                            Referências de tecnologia pública (não
                            contratáveis) — citadas nas evidências, mas não
                            disponíveis no mercado
                        </Typography>
                        {itensNaoContrataveis.map((s) => (
                            <SolucaoRow key={s.nome_solucao}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Typography
                                        sx={{fontWeight: 700, fontSize: 14}}
                                    >
                                        {s.nome_solucao}
                                    </Typography>
                                    {s.fornecedor &&
                                        s.fornecedor !== s.nome_solucao && (
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    color: '#7a8896',
                                                }}
                                            >
                                                · {s.fornecedor}
                                            </Typography>
                                        )}
                                    <Chip
                                        size="small"
                                        label="ferramenta interna de órgão público"
                                        variant="outlined"
                                    />
                                </Box>
                                {s.descricao_1_linha && (
                                    <Typography
                                        sx={{fontSize: 13, color: '#4a5563'}}
                                    >
                                        {s.descricao_1_linha}
                                    </Typography>
                                )}
                            </SolucaoRow>
                        ))}
                    </Box>
                )}
                {manuais.map((m) => (
                    <SolucaoRow key={m.nome_solucao}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                <Typography sx={{fontWeight: 700, fontSize: 14}}>
                                    {m.nome_solucao}
                                </Typography>
                                <Chip
                                    size="small"
                                    label="a incluir"
                                    variant="outlined"
                                />
                            </Box>
                            <IconButton
                                size="small"
                                aria-label={`Remover ${m.nome_solucao}`}
                                onClick={() =>
                                    setManuais((prev) =>
                                        prev.filter(
                                            (x) =>
                                                x.nome_solucao !== m.nome_solucao
                                        )
                                    )
                                }
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </SolucaoRow>
                ))}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        mt: 1.5,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                    }}
                >
                    <TextField
                        size="small"
                        placeholder="Nome da solução (ex.: Sistema X)"
                        value={manualNome}
                        onChange={(e) => setManualNome(e.target.value)}
                        sx={{minWidth: 220}}
                    />
                    <TextField
                        size="small"
                        placeholder="Site (opcional)"
                        value={manualSite}
                        onChange={(e) => setManualSite(e.target.value)}
                        sx={{minWidth: 220}}
                    />
                    <Button
                        startIcon={<AddIcon />}
                        onClick={adicionarManual}
                        disabled={!manualNome.trim()}
                        sx={{textTransform: 'none', fontWeight: 700}}
                    >
                        Adicionar solução
                    </Button>
                </Box>
                <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                    <Button
                        variant="contained"
                        disabled={
                            selecionando || investigando || totalMarcadasLocal < 1
                        }
                        startIcon={
                            selecionando ? (
                                <CircularProgress
                                    size={16}
                                    sx={{color: '#fff'}}
                                />
                            ) : undefined
                        }
                        onClick={confirmarSelecao}
                        sx={{
                            backgroundColor: ACCENT,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '10px',
                        }}
                    >
                        Comparar soluções selecionadas
                    </Button>
                </Box>

                {/* Atalho: requisitos técnicos essenciais (nem todo conceito
                    pede comparação de marcas). Destaque quando o sinal do
                    mercado aponta o caminho da licitação. */}
                {!investigando && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 1.5,
                            borderRadius: '10px',
                            background: '#f6f8fa',
                            border: '1px solid #eef1f4',
                        }}
                    >
                        {sinalConceito?.sinal === 'CAMINHO_LICITACAO' && (
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: '#14532d',
                                    mb: 0.5,
                                }}
                            >
                                Caso típico de licitação —{' '}
                                {fraseSinal(sinalConceito)} Estabeleça os
                                requisitos essenciais e siga.
                            </Typography>
                        )}
                        <Typography sx={{fontSize: 13, color: '#4a5563'}}>
                            Nem todo caso pede comparação de marcas: você pode
                            definir os requisitos técnicos essenciais do que
                            será contratado e seguir direto para o caminho
                            competitivo.
                        </Typography>
                        <Button
                            size="small"
                            variant={
                                sinalConceito?.sinal === 'CAMINHO_LICITACAO'
                                    ? 'contained'
                                    : 'outlined'
                            }
                            disabled={iniciandoRequisitos}
                            startIcon={
                                iniciandoRequisitos ? (
                                    <CircularProgress size={14} />
                                ) : undefined
                            }
                            onClick={iniciarRequisitos}
                            sx={{
                                mt: 1,
                                textTransform: 'none',
                                fontWeight: 700,
                                borderRadius: '10px',
                                ...(sinalConceito?.sinal === 'CAMINHO_LICITACAO'
                                    ? {backgroundColor: ACCENT}
                                    : {}),
                            }}
                        >
                            Definir requisitos técnicos essenciais e seguir
                        </Button>
                    </Box>
                )}

                {/* Pedido de informações ao mercado (RFI): documento formal
                    p/ o órgão — se quiser — enviar a uma empresa. O sistema
                    NUNCA envia nada. */}
                {!investigando && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 1.5,
                            borderRadius: '10px',
                            background: '#fbfcfe',
                            border: '1px solid #eef1f4',
                        }}
                    >
                        <Typography
                            sx={{fontSize: 13, fontWeight: 700, color: '#1f2b3a'}}
                        >
                            Pedido de informações ao mercado
                        </Typography>
                        <Typography sx={{fontSize: 13, color: '#4a5563'}}>
                            Elabore uma consulta formal a uma empresa do
                            mercado (informações técnicas, condições usuais e
                            referências de preço). O envio é feito pelo órgão,
                            pelos canais oficiais — o documento fica registrado
                            no levantamento.
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                mt: 1,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => abrirRfi()}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderRadius: '10px',
                                }}
                            >
                                Elaborar pedido de informações
                            </Button>
                            {itensSolucoes.slice(0, 4).map((s) => (
                                <Button
                                    key={s.nome_solucao}
                                    size="small"
                                    onClick={() => abrirRfi(s.nome_solucao)}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        color: '#7a8896',
                                    }}
                                >
                                    Pedido → {s.nome_solucao}
                                </Button>
                            ))}
                        </Box>
                        {rfis.length > 0 && (
                            <Box sx={{mt: 1}}>
                                {rfis.map((r) => (
                                    <Box
                                        key={r.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            flexWrap: 'wrap',
                                            mt: 0.5,
                                        }}
                                    >
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            color={
                                                r.status === 'PRONTO'
                                                    ? 'success'
                                                    : r.status === 'ERRO'
                                                    ? 'error'
                                                    : 'info'
                                            }
                                            label={
                                                (r.destinatario?.nome
                                                    ? `Pedido — ${r.destinatario.nome}`
                                                    : 'Pedido — destinatário a definir') +
                                                (r.status === 'GERANDO'
                                                    ? ' (elaborando…)'
                                                    : r.status === 'ERRO'
                                                    ? ' (indisponível)'
                                                    : '')
                                            }
                                        />
                                        {r.status === 'PRONTO' && (
                                            <>
                                                <Button
                                                    size="small"
                                                    onClick={() =>
                                                        setRfiEdicao({
                                                            id: r.id,
                                                            texto: r.texto ?? '',
                                                        })
                                                    }
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 700,
                                                        p: 0,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    Revisar e editar
                                                </Button>
                                                <Button
                                                    size="small"
                                                    disabled={
                                                        !!exportandoRfi[
                                                            `${r.id}:docx`
                                                        ]
                                                    }
                                                    onClick={() =>
                                                        exportarRfiArquivo(
                                                            r.id,
                                                            'docx'
                                                        )
                                                    }
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        p: 0,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    {exportandoRfi[
                                                        `${r.id}:docx`
                                                    ]
                                                        ? 'DOCX…'
                                                        : 'DOCX'}
                                                </Button>
                                                <Button
                                                    size="small"
                                                    disabled={
                                                        !!exportandoRfi[
                                                            `${r.id}:pdf`
                                                        ]
                                                    }
                                                    onClick={() =>
                                                        exportarRfiArquivo(
                                                            r.id,
                                                            'pdf'
                                                        )
                                                    }
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        p: 0,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    {exportandoRfi[`${r.id}:pdf`]
                                                        ? 'PDF…'
                                                        : 'PDF'}
                                                </Button>
                                            </>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
                {conceitoEscolhido && (
                    <Box sx={{mt: 3}}>
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#7a8896',
                                mb: 0.5,
                            }}
                        >
                            Evidências coletadas para o conceito
                        </Typography>
                        {renderCardAlternativa(conceitoEscolhido, {
                            investigacao: true,
                            evidenciaResumo: true,
                        })}
                    </Box>
                )}
            </Card>
        )
    }

    // ------------------ fluxo novo: passo 4 (comparação) ---------------------
    const renderPassoComparacao = () => {
        const aguardandoParecer =
            detail?.status === 'SOLUCOES_PRONTAS' &&
            selecionadasAtuais.length >= 2 &&
            !detail?.parecer
        return (
            <Card>
                <SectionTitle>Comparação das soluções</SectionTitle>
                {detail?.parecer?.texto ? (
                    <>
                        <Chip
                            size="small"
                            icon={<AutoAwesomeIcon sx={{fontSize: 14}} />}
                            label="Parecer da Licito.guru IA"
                            color="info"
                            sx={{fontWeight: 600, mb: 1.5}}
                        />
                        <Typography
                            sx={{
                                fontSize: 14,
                                color: '#2a3340',
                                whiteSpace: 'pre-wrap',
                                lineHeight: 1.6,
                            }}
                        >
                            {detail.parecer.texto}
                        </Typography>
                        <Typography
                            sx={{fontSize: 12, color: '#7a8896', mt: 1.5}}
                        >
                            O parecer é um apoio à decisão — a escolha da solução
                            é sua, no próximo passo.
                        </Typography>
                    </>
                ) : aguardandoParecer ? (
                    <Box>
                        <LinearProgress />
                        <Typography sx={{color: '#7a8896', fontSize: 12, mt: 0.5}}>
                            A Licito.guru IA está comparando as soluções
                            marcadas…
                        </Typography>
                    </Box>
                ) : selecionadasAtuais.length === 1 ? (
                    <Typography sx={{color: '#4a5563', fontSize: 14}}>
                        Você marcou uma única solução — o parecer comparativo é
                        dispensado. Avance para a escolha.
                    </Typography>
                ) : (
                    <Typography sx={{color: '#7a8896', fontSize: 13}}>
                        Selecione as soluções no passo anterior para comparar.
                    </Typography>
                )}
                <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                    <Button
                        variant="contained"
                        disabled={selecionadasAtuais.length < 1}
                        onClick={() => setActiveStep(4)}
                        sx={{
                            backgroundColor: ACCENT,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '10px',
                        }}
                    >
                        Ir para a escolha
                    </Button>
                </Box>
            </Card>
        )
    }

    // ------------------- fluxo novo: passo 5 (escolha) -----------------------
    // Painel supervisionado dos DIFERENCIAIS: a IA sugere com base; o gestor
    // confirma/edita/remove/acrescenta — SÓ os confirmados fundamentam a
    // justificativa ("mais completa" não basta).
    const renderPainelDiferenciais = () => (
        <Box
            sx={{
                mt: 2,
                p: 2,
                border: '1.5px solid #cfe3fa',
                borderRadius: '12px',
                background: '#f8fbff',
            }}
        >
            <SectionTitle style={{marginBottom: 8}}>
                Diferenciais da solução escolhida
            </SectionTitle>
            {avisoResponsabilidade}
            <Typography sx={{fontSize: 13, color: '#4a5563', mb: 1.5}}>
                A justificativa dirá o que{' '}
                <strong>{preferida?.nome_solucao}</strong> possui que as demais
                não possuem. Confirme, edite ou remova os diferenciais abaixo e
                acrescente os seus — só os confirmados entram na justificativa.
            </Typography>
            {difEdicao.length === 0 && (
                <Alert severity="warning" sx={{mb: 1.5, borderRadius: '10px'}}>
                    <Typography sx={{fontSize: 13, fontWeight: 700}}>
                        Não encontramos diferenciais materiais sustentáveis nas
                        evidências coletadas
                    </Typography>
                    <Typography sx={{fontSize: 13}}>
                        {preferida?.diferenciais?.sem_diferenciais_porque ||
                            'Sem apontar o que a escolhida possui que as demais ' +
                                'não possuem, a preferência não se sustenta.'}{' '}
                        Acrescente um diferencial concreto seu — ou registre a
                        escolha sem preferência (licitação com requisitos
                        técnicos mínimos).
                    </Typography>
                    {solucoesSemLeituraDeSite.length > 0 && (
                        <Box sx={{mt: 1.5}}>
                            <Typography sx={{fontSize: 13, fontWeight: 700}}>
                                Por que a análise pode ter saído vazia
                            </Typography>
                            {solucoesSemLeituraDeSite.map((s) => {
                                const ev = s.site_evidencia
                                const semSite = ev?.status === 'sem_site'
                                const siteAtual =
                                    ev?.url || s.site || undefined
                                return (
                                    <Box
                                        key={s.nome_solucao}
                                        sx={{mt: 1}}
                                        data-testid={`diagnostico-site-${s.nome_solucao}`}
                                    >
                                        <Typography sx={{fontSize: 13}}>
                                            {semSite
                                                ? `Não localizamos o site oficial de ${s.nome_solucao}.`
                                                : `Não foi possível ler o site de ${s.nome_solucao}` +
                                                  `${ev?.motivo ? ` (${ev.motivo})` : ''}.`}{' '}
                                            Confirme ou corrija o endereço
                                            abaixo e peça uma nova leitura.
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                mt: 0.5,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <TextField
                                                size="small"
                                                placeholder="Site oficial (ex.: www.empresa.com.br)"
                                                value={
                                                    siteCorrigido[
                                                        s.nome_solucao
                                                    ] ??
                                                    siteAtual ??
                                                    ''
                                                }
                                                onChange={(e) =>
                                                    setSiteCorrigido(
                                                        (prev) => ({
                                                            ...prev,
                                                            [s.nome_solucao]:
                                                                e.target.value,
                                                        })
                                                    )
                                                }
                                                sx={{
                                                    minWidth: 260,
                                                    flex: 1,
                                                    background: '#fff',
                                                }}
                                            />
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                disabled={lendoSiteDe != null}
                                                startIcon={
                                                    lendoSiteDe ===
                                                    s.nome_solucao ? (
                                                        <CircularProgress
                                                            size={14}
                                                        />
                                                    ) : undefined
                                                }
                                                onClick={() =>
                                                    lerSiteERefazer(
                                                        s.nome_solucao,
                                                        siteAtual
                                                    )
                                                }
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                }}
                                            >
                                                Ler site e refazer análise
                                            </Button>
                                        </Box>
                                    </Box>
                                )
                            })}
                        </Box>
                    )}
                    <Button
                        size="small"
                        disabled={escolhendoSolucao}
                        onClick={() => escolherSolucaoFinal(SEM_PREFERENCIA)}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            mt: 0.5,
                            p: 0,
                        }}
                    >
                        Seguir sem preferência →
                    </Button>
                </Alert>
            )}
            {difEdicao.map((d, i) => (
                <SolucaoRow key={i}>
                    <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
                        <Checkbox
                            size="small"
                            checked={d.incluido}
                            onChange={(e) =>
                                setDifEdicao((prev) =>
                                    prev.map((x, j) =>
                                        j === i
                                            ? {...x, incluido: e.target.checked}
                                            : x
                                    )
                                )
                            }
                            sx={{mt: -0.5}}
                        />
                        <Box sx={{flex: 1}}>
                            <TextField
                                fullWidth
                                size="small"
                                multiline
                                value={d.texto}
                                onChange={(e) =>
                                    setDifEdicao((prev) =>
                                        prev.map((x, j) =>
                                            j === i
                                                ? {...x, texto: e.target.value}
                                                : x
                                        )
                                    )
                                }
                            />
                            {d.base && (
                                <Typography
                                    sx={{fontSize: 12, color: '#7a8896', mt: 0.5}}
                                >
                                    Base: {d.base}
                                </Typography>
                            )}
                        </Box>
                        <IconButton
                            size="small"
                            aria-label="Remover diferencial"
                            onClick={() =>
                                setDifEdicao((prev) =>
                                    prev.filter((_x, j) => j !== i)
                                )
                            }
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </SolucaoRow>
            ))}
            <Box sx={{display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap'}}>
                <TextField
                    size="small"
                    placeholder="Novo diferencial concreto (o que ela possui a mais)"
                    value={difNovoTexto}
                    onChange={(e) => setDifNovoTexto(e.target.value)}
                    sx={{minWidth: 280, flex: 1}}
                />
                <TextField
                    size="small"
                    placeholder="Base (ex.: uso no setor, contrato anterior)"
                    value={difNovaBase}
                    onChange={(e) => setDifNovaBase(e.target.value)}
                    sx={{minWidth: 220}}
                />
                <Button
                    startIcon={<AddIcon />}
                    disabled={!difNovoTexto.trim()}
                    onClick={() => {
                        setDifEdicao((prev) => [
                            ...prev,
                            {
                                texto: difNovoTexto.trim(),
                                base: difNovaBase.trim(),
                                origem: 'gestor',
                                incluido: true,
                            },
                        ])
                        setDifNovoTexto('')
                        setDifNovaBase('')
                    }}
                    sx={{textTransform: 'none', fontWeight: 700}}
                >
                    Adicionar
                </Button>
            </Box>
            <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                <Button
                    variant="contained"
                    disabled={
                        confirmandoDif ||
                        !difEdicao.some((d) => d.incluido && d.texto.trim())
                    }
                    startIcon={
                        confirmandoDif ? (
                            <CircularProgress size={16} sx={{color: '#fff'}} />
                        ) : (
                            <AutoAwesomeIcon />
                        )
                    }
                    onClick={confirmarDiferenciais}
                    sx={{
                        backgroundColor: ACCENT,
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: '10px',
                    }}
                >
                    Confirmar diferenciais e gerar justificativa
                </Button>
            </Box>
        </Box>
    )

    // Painel supervisionado dos REQUISITOS TÉCNICOS ESSENCIAIS (caminho
    // competitivo — motor comum do sem-preferência e do atalho de Soluções).
    const renderPainelRequisitos = () => (
        <Box
            sx={{
                mt: 2,
                p: 2,
                border: '1.5px solid #cfe3fa',
                borderRadius: '12px',
                background: '#f8fbff',
            }}
        >
            <SectionTitle style={{marginBottom: 8}}>
                Requisitos técnicos essenciais
            </SectionTitle>
            {avisoResponsabilidade}
            <Typography sx={{fontSize: 13, color: '#4a5563', mb: 1.5}}>
                A contratação seguirá competitiva, sem indicação de marca: os
                requisitos abaixo (derivados das evidências do levantamento)
                especificam o objeto. Os marcados como{' '}
                <strong>Recomendado</strong> são apenas uma sugestão da
                Licito.guru IA (já vêm pré-marcados); exigir cada requisito é
                decisão sua. Confirme, ajuste ou acrescente os seus.
            </Typography>
            {reqEdicao.length === 0 && (
                <Alert severity="warning" sx={{mb: 1.5, borderRadius: '10px'}}>
                    <Typography sx={{fontSize: 13}}>
                        A Licito.guru IA não sugeriu requisitos agora — escreva
                        abaixo os requisitos técnicos essenciais do que será
                        contratado.
                    </Typography>
                </Alert>
            )}
            {reqEdicao.map((r, i) => (
                <SolucaoRow key={i}>
                    <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
                        <Checkbox
                            size="small"
                            checked={r.incluido}
                            onChange={(e) =>
                                setReqEdicao((prev) =>
                                    prev.map((x, j) =>
                                        j === i
                                            ? {...x, incluido: e.target.checked}
                                            : x
                                    )
                                )
                            }
                            sx={{mt: -0.5}}
                        />
                        <Box sx={{flex: 1, minWidth: 0}}>
                            {/* Selo "Recomendado" (sugestão da IA, pré-marcado);
                                os não-recomendados aparecem sem o selo e
                                desmarcados — o gestor decide o que exigir. */}
                            {r.recomendado && (
                                <Chip
                                    size="small"
                                    icon={
                                        <AutoAwesomeIcon sx={{fontSize: 13}} />
                                    }
                                    label="Recomendado"
                                    color="success"
                                    variant="outlined"
                                    sx={{fontWeight: 600, mb: 0.5}}
                                />
                            )}
                            <TextField
                                fullWidth
                                size="small"
                                multiline
                                value={r.texto}
                                onChange={(e) =>
                                    setReqEdicao((prev) =>
                                        prev.map((x, j) =>
                                            j === i
                                                ? {...x, texto: e.target.value}
                                                : x
                                        )
                                    )
                                }
                            />
                            {r.justificativa && r.justificativa.trim() && (
                                <Typography
                                    sx={{
                                        fontSize: 11.5,
                                        color: '#7a8896',
                                        mt: 0.5,
                                        ml: 0.25,
                                    }}
                                >
                                    {r.recomendado
                                        ? 'Por que recomendamos: '
                                        : ''}
                                    {r.justificativa}
                                </Typography>
                            )}
                        </Box>
                        <IconButton
                            size="small"
                            aria-label="Remover requisito"
                            onClick={() =>
                                setReqEdicao((prev) =>
                                    prev.filter((_x, j) => j !== i)
                                )
                            }
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </SolucaoRow>
            ))}
            <Box sx={{display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap'}}>
                <TextField
                    size="small"
                    placeholder="Novo requisito técnico essencial (sem marca)"
                    value={reqNovoTexto}
                    onChange={(e) => setReqNovoTexto(e.target.value)}
                    sx={{minWidth: 280, flex: 1}}
                />
                <Button
                    startIcon={<AddIcon />}
                    disabled={!reqNovoTexto.trim()}
                    onClick={() => {
                        setReqEdicao((prev) => [
                            ...prev,
                            {
                                texto: reqNovoTexto.trim(),
                                origem: 'gestor',
                                incluido: true,
                            },
                        ])
                        setReqNovoTexto('')
                    }}
                    sx={{textTransform: 'none', fontWeight: 700}}
                >
                    Adicionar
                </Button>
            </Box>
            <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                <Button
                    variant="contained"
                    disabled={
                        confirmandoReq ||
                        !reqEdicao.some((r) => r.incluido && r.texto.trim())
                    }
                    startIcon={
                        confirmandoReq ? (
                            <CircularProgress size={16} sx={{color: '#fff'}} />
                        ) : (
                            <AutoAwesomeIcon />
                        )
                    }
                    onClick={confirmarRequisitos}
                    sx={{
                        backgroundColor: ACCENT,
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: '10px',
                    }}
                >
                    Confirmar requisitos e gerar justificativa
                </Button>
            </Box>
        </Box>
    )

    const renderPassoEscolhaSolucao = () => (
        <Card>
            <SectionTitle>Escolha da solução</SectionTitle>
            {avisoResponsabilidade}
            <Typography sx={{color: '#7a8896', fontSize: 13, mb: 2}}>
                Escolha a solução preferida entre as comparadas — ou registre
                que não há preferência — e anote suas observações. A decisão é
                sua; a Licito.guru IA apoia com os diferenciais e a
                justificativa.
            </Typography>
            <RadioGroup
                value={
                    solucaoEscolhida ||
                    (preferida?.sem_preferencia
                        ? SEM_PREFERENCIA
                        : preferida?.nome_solucao) ||
                    ''
                }
                onChange={(e) => setSolucaoEscolhida(e.target.value)}
            >
                {selecionadasAtuais.map((s) => (
                    <FormControlLabel
                        key={s.nome_solucao}
                        value={s.nome_solucao}
                        control={<Radio />}
                        disabled={gerandoJustificativa || escolhendoSolucao}
                        label={
                            <Box sx={{py: 0.5}}>
                                <Typography sx={{fontWeight: 600, fontSize: 14}}>
                                    {s.nome_solucao}
                                    {s.fornecedor &&
                                    s.fornecedor !== s.nome_solucao
                                        ? ` · ${s.fornecedor}`
                                        : ''}
                                </Typography>
                                {(s.contratacoes_pncp ?? 0) > 0 && (
                                    <>
                                        <Typography
                                            sx={{fontSize: 12, color: '#7a8896'}}
                                        >
                                            {rotuloContagem(s).numero}{' '}
                                            contratação(ões) no PNCP
                                            {(s.modalidades_pncp
                                                ?.inexigibilidade ?? 0) > 0
                                                ? ` — ${s.modalidades_pncp?.inexigibilidade} de ${s.modalidades_pncp?.total} por inexigibilidade`
                                                : ''}
                                        </Typography>
                                        <RessalvaContagem s={s} />
                                    </>
                                )}
                            </Box>
                        }
                    />
                ))}
                {/* Cartão "Sem preferência" (decisão do dono): sem critério
                    técnico p/ preferir, as soluções competem no certame. */}
                <FormControlLabel
                    value={SEM_PREFERENCIA}
                    control={<Radio />}
                    disabled={gerandoJustificativa || escolhendoSolucao}
                    label={
                        <Box
                            sx={{
                                py: 0.5,
                                px: 1.5,
                                my: 0.5,
                                border: '1.5px dashed #c3ccd6',
                                borderRadius: '10px',
                                background: '#fbfcfe',
                            }}
                        >
                            <Typography sx={{fontWeight: 700, fontSize: 14}}>
                                Nenhuma das soluções — seguir para licitação com
                                requisitos técnicos mínimos
                            </Typography>
                            <Typography sx={{fontSize: 12, color: '#7a8896'}}>
                                Registra que NÃO há preferência por ausência de
                                critério técnico justificável: as soluções
                                identificadas deverão competir no certame, com
                                requisitos derivados da comparação (sem marca).
                            </Typography>
                        </Box>
                    }
                />
            </RadioGroup>
            <FieldLabel style={{marginTop: 12}}>
                Observações do gestor (opcional)
            </FieldLabel>
            <Typography sx={{fontSize: 12, color: '#7a8896', mb: 0.5}}>
                Suas observações são matéria-prima da análise (ex.: "mais
                completa" vira critério concreto quando as evidências
                sustentam) — nunca texto final.
            </Typography>
            <TextField
                fullWidth
                multiline
                minRows={2}
                placeholder="Ex.: já usamos essa solução em outro setor…"
                value={obsSolucao}
                onChange={(e) => setObsSolucao(e.target.value)}
                disabled={gerandoJustificativa || escolhendoSolucao}
            />
            <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                <Button
                    variant="contained"
                    startIcon={
                        escolhendoSolucao ? (
                            <CircularProgress size={16} sx={{color: '#fff'}} />
                        ) : (
                            <AutoAwesomeIcon />
                        )
                    }
                    disabled={
                        escolhendoSolucao ||
                        gerandoJustificativa ||
                        !(
                            solucaoEscolhida ||
                            preferida?.nome_solucao ||
                            preferida?.sem_preferencia
                        )
                    }
                    onClick={() => escolherSolucaoFinal()}
                    sx={{
                        backgroundColor: ACCENT,
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: '10px',
                    }}
                >
                    Registrar escolha e avançar
                </Button>
            </Box>
            {aguardandoDiferenciais && (
                <Box sx={{mt: 2}}>
                    <LinearProgress />
                    <Typography sx={{color: '#7a8896', fontSize: 12, mt: 0.5}}>
                        A Licito.guru IA está identificando os diferenciais da
                        solução escolhida…
                    </Typography>
                </Box>
            )}
            {aguardandoRequisitos && (
                <Box sx={{mt: 2}}>
                    <LinearProgress />
                    <Typography sx={{color: '#7a8896', fontSize: 12, mt: 0.5}}>
                        A Licito.guru IA está derivando os requisitos técnicos
                        essenciais das evidências…
                    </Typography>
                </Box>
            )}
            {painelDiferenciaisAberto && renderPainelDiferenciais()}
            {painelRequisitosAberto && renderPainelRequisitos()}
            {gerandoJustificativa && (
                <Box sx={{mt: 2}}>
                    <LinearProgress />
                    <Typography sx={{color: '#7a8896', fontSize: 12, mt: 0.5}}>
                        A Licito.guru IA está redigindo a justificativa…
                    </Typography>
                </Box>
            )}
            {renderBlocoJustificativa()}
        </Card>
    )

    // ------------------------------------------------------- passo 4: escolha
    const renderPasso4 = () => (
        <Card>
            <SectionTitle>Escolha da melhor solução</SectionTitle>
            {avisoResponsabilidade}
            <Typography sx={{color: '#7a8896', fontSize: 13, mb: 2}}>
                Marque a alternativa escolhida e registre suas observações. A
                Licito.guru IA redige a justificativa técnica e econômica (art.
                18, §1º, V) — e você edita o texto final.
            </Typography>
            <RadioGroup
                value={escolhaId}
                onChange={(e) => setEscolhaId(e.target.value)}
            >
                {alternativasAtivas.map((alt) => (
                    <FormControlLabel
                        key={alt.id}
                        value={alt.id}
                        control={<Radio />}
                        disabled={gerandoJustificativa}
                        label={
                            <Box sx={{py: 0.5}}>
                                <Typography sx={{fontWeight: 600, fontSize: 14}}>
                                    {alt.titulo}
                                    {alt.faixa_preco
                                        ? ` · mediana ${currency(alt.faixa_preco.mediana)}`
                                        : ''}
                                    {in65Info(alt)?.status === 'CONCLUIDO' &&
                                    in65Info(alt)?.valor_total_estimado != null
                                        ? ` · IN 65 ${currency(in65Info(alt)?.valor_total_estimado)}`
                                        : ''}
                                </Typography>
                                <Typography sx={{fontSize: 12, color: '#7a8896'}}>
                                    {TIPO_LABEL[alt.tipo] ?? alt.tipo}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1,
                                        mt: 0.5,
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    {renderVinculoIn65(alt)}
                                    <Button
                                        size="small"
                                        onClick={(e) => {
                                            // Dentro do label do radio: não
                                            // seleciona a alternativa ao clicar.
                                            e.preventDefault()
                                            e.stopPropagation()
                                            pesquisarPrecosDaAlternativa(alt)
                                        }}
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: '#7a8896',
                                            p: 0,
                                            minWidth: 0,
                                        }}
                                    >
                                        Pesquisar preços (IN 65)
                                    </Button>
                                </Box>
                            </Box>
                        }
                    />
                ))}
            </RadioGroup>
            <FieldLabel style={{marginTop: 12}}>
                Observações do gestor (opcional)
            </FieldLabel>
            <TextField
                fullWidth
                multiline
                minRows={2}
                placeholder="Ex.: a equipe é reduzida, preferimos solução sem operação própria…"
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                disabled={gerandoJustificativa}
            />
            <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                <Button
                    variant="contained"
                    startIcon={
                        gerandoJustificativa ? (
                            <CircularProgress size={16} sx={{color: '#fff'}} />
                        ) : (
                            <AutoAwesomeIcon />
                        )
                    }
                    disabled={gerandoJustificativa || !escolhaId}
                    onClick={gerarJustificativa}
                    sx={{
                        backgroundColor: ACCENT,
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: '10px',
                    }}
                >
                    Gerar justificativa com a Licito.guru IA
                </Button>
            </Box>
            {gerandoJustificativa && (
                <Box sx={{mt: 2}}>
                    <LinearProgress />
                    <Typography sx={{color: '#7a8896', fontSize: 12, mt: 0.5}}>
                        A Licito.guru IA está redigindo a justificativa técnica
                        e econômica…
                    </Typography>
                </Box>
            )}
            {renderBlocoJustificativa()}
        </Card>
    )

    // ----------------------------------------------------- passo 5: relatório
    const renderPasso5 = () => (
        <Card>
            <SectionTitle>Relatório de Levantamento de Mercado</SectionTitle>
            {detail?.status !== 'CONCLUIDO' ? (
                <>
                    <Typography sx={{color: '#7a8896', fontSize: 13, mb: 2}}>
                        Ao concluir, o relatório consolida problema, mapa de
                        alternativas, evidências datadas e a justificativa da
                        escolha — pronto para juntar ao ETP.
                    </Typography>
                    <Button
                        variant="contained"
                        disabled={concluindo || !detail?.justificativa_final}
                        onClick={concluir}
                        startIcon={
                            concluindo ? (
                                <CircularProgress size={16} sx={{color: '#fff'}} />
                            ) : undefined
                        }
                        sx={{
                            backgroundColor: ACCENT,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '10px',
                        }}
                    >
                        Concluir levantamento
                    </Button>
                    {!detail?.justificativa_final && (
                        <Typography sx={{color: '#7a8896', fontSize: 12, mt: 1}}>
                            Gere a justificativa no passo anterior antes de
                            concluir.
                        </Typography>
                    )}
                </>
            ) : (
                <>
                    <Box sx={{display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2}}>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadOutlinedIcon />}
                            onClick={() =>
                                detail &&
                                api
                                    .exportarDocx(detail.id)
                                    .catch((e) => setErro(msgDeErro(e)))
                            }
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                borderRadius: '10px',
                            }}
                        >
                            Baixar DOCX
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadOutlinedIcon />}
                            onClick={() =>
                                detail &&
                                api
                                    .exportarPdf(detail.id)
                                    .catch((e) => setErro(msgDeErro(e)))
                            }
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                borderRadius: '10px',
                            }}
                        >
                            Baixar PDF
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<RequestQuoteOutlinedIcon />}
                            onClick={() => {
                                // Mesmo mecanismo por alternativa, apontando a escolhida.
                                const escolhida = detail?.alternativas.find(
                                    (a) =>
                                        a.id ===
                                        (detail?.solucao_escolhida_id || escolhaId)
                                )
                                if (escolhida) pesquisarPrecosDaAlternativa(escolhida)
                            }}
                            sx={{
                                backgroundColor: ACCENT,
                                textTransform: 'none',
                                fontWeight: 700,
                                borderRadius: '10px',
                            }}
                        >
                            Criar Pesquisa de Preços da solução escolhida
                        </Button>
                    </Box>
                    {textosFinais?.descricao_objeto && (
                        <>
                            {textosFinais.inspiracao_pncp && (
                                <Typography
                                    sx={{fontSize: 12, color: '#7a8896', mb: 1}}
                                >
                                    Redação inspirada em contratações públicas
                                    reais da mesma solução (PNCP) — as fontes
                                    estão listadas no relatório.
                                </Typography>
                            )}
                            {renderCardTextoFinal(
                                'objeto',
                                'Descrição completa do objeto',
                                textosFinais.descricao_objeto
                            )}
                            {renderCardTextoFinal(
                                'trecho',
                                'Trecho: levantamento de mercado (para o formulário)',
                                textosFinais.trecho_levantamento || ''
                            )}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    flexWrap: 'wrap',
                                    mb: 2,
                                }}
                            >
                                <Button
                                    variant="contained"
                                    startIcon={<DescriptionOutlinedIcon />}
                                    onClick={() => navigate('/gerador')}
                                    sx={{
                                        backgroundColor: ACCENT,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        borderRadius: '10px',
                                    }}
                                >
                                    Gerar documentos desta contratação →
                                </Button>
                                <Typography sx={{color: '#7a8896', fontSize: 12}}>
                                    Cole a descrição do objeto e o trecho do
                                    levantamento nos campos do formulário.
                                </Typography>
                            </Box>
                        </>
                    )}
                    {relatorioLoading ? (
                        <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : relatorioErro ? (
                        <Box sx={{textAlign: 'center', py: 3}}>
                            <Typography sx={{color: '#7a8896', fontSize: 13, mb: 1.5}}>
                                Não foi possível carregar o relatório agora.
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={tentarRelatorioDeNovo}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderRadius: '10px',
                                }}
                            >
                                Tentar novamente
                            </Button>
                        </Box>
                    ) : relatorioHtml ? (
                        // sandbox vazio: o relatório é HTML ESTÁTICO imprimível
                        // vindo do backend com conteúdo de terceiros embutido —
                        // sem scripts e em origem opaca (nunca a origem do app,
                        // onde o cookie de sessão é legível por JS).
                        <iframe
                            title="Relatório de Levantamento de Mercado"
                            sandbox=""
                            srcDoc={relatorioHtml}
                            style={{
                                width: '100%',
                                height: '70vh',
                                border: '1px solid #e6e9ee',
                                borderRadius: 8,
                                background: '#fff',
                            }}
                        />
                    ) : (
                        <Typography sx={{color: '#7a8896', fontSize: 13}}>
                            O relatório aparecerá aqui em instantes.
                        </Typography>
                    )}
                </>
            )}
        </Card>
    )

    // ---------------------------------------------------------------- detail
    const renderDetail = () => (
        <>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2}}>
                <IconButton onClick={voltarParaLista} aria-label="Voltar para a lista">
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" sx={{fontWeight: 700, color: '#1f2b3a'}}>
                    Levantamento de Mercado
                </Typography>
                {detail && (
                    <Chip
                        size="small"
                        label={rotuloStatus(detail)}
                        color={statusColor(detail.status)}
                        sx={{ml: 1}}
                    />
                )}
                {detail && (
                    <Tooltip title="Atualizar">
                        <span>
                            <IconButton
                                size="small"
                                onClick={atualizarDetalhe}
                                disabled={atualizando}
                                aria-label="Atualizar levantamento"
                                sx={{color: '#7a8896'}}
                            >
                                {atualizando ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <RefreshIcon fontSize="small" />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Box>

            {detail?.status === 'ERRO' && (
                <Alert severity="error" sx={{mb: 2}}>
                    Algo deu errado no processamento deste levantamento. Tente
                    novamente ou fale com o suporte.
                </Alert>
            )}

            <Card>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {(fluxoNovo ? STEP_LABELS_NOVO : STEP_LABELS).map(
                        (label, i) => {
                            // Navegável = etapa já alcançada (índice ≤ máximo):
                            // permite voltar E reavançar livremente. As etapas
                            // ainda não atingidas ficam cinza / não clicáveis.
                            const navegavel = i <= maxStepAlcancado
                            return (
                                <Step
                                    key={label}
                                    completed={i < maxStepAlcancado}
                                    sx={{
                                        cursor: navegavel
                                            ? 'pointer'
                                            : 'default',
                                    }}
                                    onClick={() => {
                                        if (navegavel) setActiveStep(i)
                                    }}
                                >
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            )
                        }
                    )}
                </Stepper>
            </Card>

            {detailLoading ? (
                <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                    <CircularProgress size={30} />
                </Box>
            ) : (
                <>
                    {activeStep === 0 && renderPasso1()}
                    {fluxoNovo ? (
                        <>
                            {activeStep === 1 && renderPassoConceitos()}
                            {activeStep === 2 && renderPassoSolucoes()}
                            {activeStep === 3 && renderPassoComparacao()}
                            {activeStep === 4 && renderPassoEscolhaSolucao()}
                            {activeStep === 5 && renderPasso5()}
                        </>
                    ) : (
                        <>
                            {activeStep === 1 && renderPasso2()}
                            {activeStep === 2 && renderPasso3()}
                            {activeStep === 3 && renderPasso4()}
                            {activeStep === 4 && renderPasso5()}
                        </>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 4,
                        }}
                    >
                        <Button
                            disabled={activeStep === 0}
                            onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
                            sx={{textTransform: 'none', fontWeight: 700}}
                        >
                            Voltar
                        </Button>
                        {activeStep < (fluxoNovo ? 5 : 4) && (
                            <Button
                                disabled={!podeAvancar(activeStep)}
                                onClick={() =>
                                    setActiveStep((s) =>
                                        Math.min(fluxoNovo ? 5 : 4, s + 1)
                                    )
                                }
                                sx={{textTransform: 'none', fontWeight: 700}}
                            >
                                Avançar
                            </Button>
                        )}
                    </Box>
                </>
            )}
        </>
    )

    return (
        <Container>
            {view === 'list' ? renderLista() : renderDetail()}

            <Dialog
                open={altDialogOpen}
                onClose={() => setAltDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {altEditando ? 'Editar alternativa' : 'Adicionar alternativa'}
                </DialogTitle>
                <DialogContent>
                    <FieldLabel style={{marginTop: 8}}>Título</FieldLabel>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Ex.: Contratar serviço de digitalização com fé pública"
                        value={altForm.titulo}
                        onChange={(e) =>
                            setAltForm((f) => ({...f, titulo: e.target.value}))
                        }
                    />
                    <FieldLabel style={{marginTop: 14}}>Descrição</FieldLabel>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        placeholder="Descreva o modelo de fornecimento desta alternativa…"
                        value={altForm.descricao}
                        onChange={(e) =>
                            setAltForm((f) => ({...f, descricao: e.target.value}))
                        }
                    />
                    <FieldLabel style={{marginTop: 14}}>Tipo</FieldLabel>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        value={altForm.tipo}
                        onChange={(e) =>
                            setAltForm((f) => ({
                                ...f,
                                tipo: e.target.value as TipoAlternativa,
                            }))
                        }
                    >
                        {TIPOS.map((t) => (
                            <MenuItem key={t} value={t}>
                                {TIPO_LABEL[t]}
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setAltDialogOpen(false)}
                        sx={{textTransform: 'none'}}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        disabled={altSalvando}
                        onClick={salvarAlternativa}
                        sx={{
                            backgroundColor: ACCENT,
                            textTransform: 'none',
                            fontWeight: 700,
                        }}
                    >
                        {altSalvando ? 'Salvando…' : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* RFI: gerar pedido de informações (destinatário opcional). */}
            <Dialog
                open={rfiDialogAberto}
                onClose={() => setRfiDialogAberto(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Pedido de informações ao mercado</DialogTitle>
                <DialogContent>
                    {avisoResponsabilidade}
                    <Typography sx={{fontSize: 13, color: '#4a5563', mb: 1.5}}>
                        A Licito.guru IA redige um ofício formal de consulta ao
                        mercado (art. 18 da Lei 14.133/2021), com espaços para o
                        órgão preencher. O sistema NÃO envia nada — o envio é
                        feito pelo órgão, pelos canais oficiais.
                    </Typography>
                    <FieldLabel>Empresa destinatária (opcional)</FieldLabel>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Ex.: nome da empresa/solução — vazio = documento com espaço a preencher"
                        value={rfiDestNome}
                        onChange={(e) => setRfiDestNome(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setRfiDialogAberto(false)}
                        sx={{textTransform: 'none'}}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        disabled={rfiGerando}
                        startIcon={
                            rfiGerando ? (
                                <CircularProgress size={16} sx={{color: '#fff'}} />
                            ) : (
                                <AutoAwesomeIcon />
                            )
                        }
                        onClick={gerarRfi}
                        sx={{
                            backgroundColor: ACCENT,
                            textTransform: 'none',
                            fontWeight: 700,
                        }}
                    >
                        Elaborar pedido
                    </Button>
                </DialogActions>
            </Dialog>

            {/* RFI: revisar/editar o ofício gerado (100% editável). */}
            <Dialog
                open={!!rfiEdicao}
                onClose={() => setRfiEdicao(null)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>Revisar pedido de informações</DialogTitle>
                <DialogContent>
                    {avisoResponsabilidade}
                    <TextField
                        fullWidth
                        multiline
                        minRows={12}
                        maxRows={24}
                        value={rfiEdicao?.texto ?? ''}
                        onChange={(e) =>
                            setRfiEdicao((prev) =>
                                prev ? {...prev, texto: e.target.value} : prev
                            )
                        }
                        sx={{'& textarea': {fontSize: 13.5}}}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setRfiEdicao(null)}
                        sx={{textTransform: 'none'}}
                    >
                        Fechar
                    </Button>
                    <Button
                        variant="contained"
                        disabled={rfiSalvando || !rfiEdicao?.texto?.trim()}
                        onClick={salvarRfi}
                        sx={{
                            backgroundColor: ACCENT,
                            textTransform: 'none',
                            fontWeight: 700,
                        }}
                    >
                        {rfiSalvando ? 'Salvando…' : 'Salvar alterações'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!erro}
                autoHideDuration={8000}
                onClose={() => setErro(null)}
                anchorOrigin={{vertical: 'top', horizontal: 'center'}}
            >
                <Alert severity="error" onClose={() => setErro(null)}>
                    {erro}
                </Alert>
            </Snackbar>

        </Container>
    )
}
